// API service layer for Marva platform backend communication

import { API_BASE_URL } from '../config/env'
import type { DecisionClass, Disposition, Receipt } from '@marva/shared/decision-receipt'
import type { LedgerChannel, LedgerFilter } from '@marva/shared/ledger-vocabulary'
import type { MerchantAskOutcome } from '@marva/shared/merchant-ask-outcomes'
import type { ReplySendState } from '@marva/shared/reply-vocabulary'
import type { FeedbackCategory } from '../lib/feedbackCategories'

// NOTE: this used to be `import.meta.env.VITE_API_BASE_URL || 'https://api.dev.marva.co'`.
// That default is a DEV url, so a production bundle built without the variable would have
// called the dev API and looked entirely healthy doing it (MAR-150).

// Types for API requests and responses
export interface CreateAccountRequest {
  name: string
  email: string
  plan: 'starter' | 'pro' | 'enterprise'
  clerkUserId?: string  // Map to Clerk user ID for authentication
}

/**
 * Display names for the creator's owner row. `POST /workspaces` writes that row itself
 * (MAR-250), taking `clerkUserId` and email from the caller's verified identity — so neither
 * belongs here.
 */
export interface WorkspaceCreator {
  firstName?: string
  lastName?: string
  name?: string
}

export interface CreateWorkspaceRequest {
  accountId: string
  name: string
  mode?: 'shadow' | 'live'
  creator?: WorkspaceCreator

  shopify?: {
    storeUrl: string
    apiKey?: string
    apiSecret?: string
    webhookSecret?: string
  }
  webhooks?: {
    status: 'connected' | 'disconnected' | 'unknown'
    lastChecked?: string
  }
}

export interface CreateUserRequest {
  accountId: string
  workspaceId: string
  email: string
  name: string
  firstName?: string
  lastName?: string
  role: 'owner' | 'admin' | 'member'
  clerkUserId?: string  // Link to Clerk user for invited users
}

export interface InviteUserRequest {
  accountId: string
  workspaceId: string
  email: string
  role: 'owner' | 'admin' | 'member'
}

export interface UpdateUserRoleRequest {
  role: 'owner' | 'admin' | 'member'
}

export interface WorkspaceUser {
  id: string
  accountId: string
  workspaceId: string
  email: string
  emailAddress?: string
  name: string | null
  firstName: string | null
  lastName: string | null
  role: 'owner' | 'admin' | 'member'
  clerkUserId: string | null
  status?: 'active' | 'pending'
  invitedAt?: string
  createdAt: string
}

// New interface for fetching user memberships
export interface GetUserMembershipsRequest {
  clerkUserId: string
}

// Response for user memberships
export interface UserMembershipsResponse {
  clerkUserId: string
  accounts: {
    account: any  // Account object
    workspaces: {
      workspace: any  // Workspace object
      userRole: 'owner' | 'admin' | 'member'
      userId: string
    }[]
  }[]
}

export interface SyncRequest {
  accountId: string
  workspaceId: string
  type: 'customers' | 'products' | 'orders'
}

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
  error?: string
  /**
   * The HTTP status, when the response reached us at all (absent on a network error).
   *
   * ⚠️ Added because `success: false` conflates two different facts and a caller cannot
   * tell them apart from `error`, which is prose. `GET /workspaces/{id}/email/domain`
   * answers **404** for a workspace that simply has no sending domain yet
   * (`services/email/domain-verification-api/src/index.js:289`) — an ordinary, expected
   * state — and 5xx when the read genuinely failed. Collapsing both into "no domain" is
   * how a verified domain came to read "Set up" after a transient failure.
   *
   * Additive and optional, exactly as `details` was: no existing caller reads it.
   */
  status?: number
  /**
   * The machine-readable half of a refusal, passed through verbatim from the service.
   *
   * ⚠️ `error` is prose written for a human and is NOT a contract — it gets reworded. The
   * services put the stable token in `details.reason` (`subscription_inactive`,
   * `authority_write_conflict`, `workspace_disabled`, …), and this client used to drop the
   * whole object on the floor, leaving callers to string-match the prose or, worse, guess
   * the condition client-side. The Authority screen branches on `details.reason`.
   *
   * ⚠️ Typed `unknown` because it is not always an object: the governance API passes Zod's
   * `error.errors` — an ARRAY — as the details of a 400. Narrow it before reading a field.
   */
  details?: unknown
  /**
   * A top-level machine-readable refusal token, for the services that send one beside
   * `error` rather than inside `details` — merchant-chat's `message_too_long` 400
   * (`{ success:false, error, code, maxChars }`). Passed through verbatim; absent when
   * the body carried none, so no existing caller sees a change.
   */
  code?: string
  /** The limit a `message_too_long` refusal names. See `code`. */
  maxChars?: number
  /**
   * The caller's membership row a create wrote beside `data` — today only `POST /workspaces`,
   * which returns the creator's `owner` row as top-level `membership` (with `membershipId`)
   * for a signed-in creator (MAR-250). Absent for API-key and Marva-admin creations, and on
   * every other endpoint, so no existing caller sees a change.
   */
  membership?: WorkspaceUser
  membershipId?: string
  /** Non-fatal work that could not finish after the primary write committed. */
  warnings?: string[]
}

/**
 * `membership` and `membershipId` from a success body, only when they are the right shape.
 * Spread into a success so that a body without them adds no keys at all.
 */
function membershipFields(data: unknown): Pick<ApiResponse, 'membership' | 'membershipId'> {
  if (!data || typeof data !== 'object') return {}
  const { membership, membershipId } = data as { membership?: unknown; membershipId?: unknown }
  const row = membership as Partial<WorkspaceUser> | null | undefined
  return {
    ...(row && typeof row === 'object' && typeof row.id === 'string' && typeof row.role === 'string'
      ? { membership: row as WorkspaceUser }
      : {}),
    ...(typeof membershipId === 'string' ? { membershipId } : {}),
  }
}

function warningFields(data: unknown): Pick<ApiResponse, 'warnings'> {
  if (!data || typeof data !== 'object') return {}
  const warnings = (data as { warnings?: unknown }).warnings
  return Array.isArray(warnings) && warnings.every(value => typeof value === 'string') ? { warnings } : {}
}

/**
 * `code` and `maxChars` from an error body, only when they are the right type. Spread
 * into a failure so that a body without them adds no keys at all.
 */
function refusalFields(data: unknown): Pick<ApiResponse, 'code' | 'maxChars'> {
  if (!data || typeof data !== 'object') return {}
  const { code, maxChars } = data as { code?: unknown; maxChars?: unknown }
  return {
    ...(typeof code === 'string' ? { code } : {}),
    ...(typeof maxChars === 'number' && Number.isFinite(maxChars) ? { maxChars } : {}),
  }
}

/**
 * The body's own `message` on a failure, carried on BOTH failure shapes.
 *
 * ⚠️ The two shapes fold the body differently into `error` — an HTTP error prefers `message`,
 * a 200 with `success: false` keeps only `error` — and `error` is left exactly as it was, since
 * callers read it. A caller that wants the service's merchant-facing sentence whatever status
 * it came with reads `message ?? error` (PR #237 round 3: the 200 shape used to drop it). Only a
 * non-empty string is carried, so a body without one leaves `message` absent, never `''`.
 */
function failureMessage(data: unknown): Pick<ApiResponse, 'message'> {
  if (!data || typeof data !== 'object') return {}
  const { message } = data as { message?: unknown }
  return typeof message === 'string' && message.trim() !== '' ? { message } : {}
}

// Token getter function type - will be set by the app after Clerk initializes
type TokenGetter = () => Promise<string | null>

// HTTP client with error handling
class ApiClient {
  private baseUrl: string
  private getToken: TokenGetter | null = null

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  // Set the token getter function (called from App.tsx after Clerk initializes)
  setTokenGetter(getter: TokenGetter) {
    this.getToken = getter
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseUrl}${endpoint}`

      // Build headers with auth token if available
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      }

      // Add Authorization header if we have a token getter
      if (this.getToken) {
        const token = await this.getToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      }

      const response = await fetch(url, {
        ...options,
        headers,
      })

      // Handle 204 No Content (e.g., DELETE responses)
      if (response.status === 204) {
        return {
          success: true,
          data: undefined,
        }
      }

      // Try to parse JSON, handle empty responses
      let data: any
      const text = await response.text()
      if (text) {
        try {
          data = JSON.parse(text)
        } catch {
          data = { message: text }
        }
      }

      if (!response.ok) {
        return {
          success: false,
          error: data?.message || data?.error || `HTTP ${response.status}: ${response.statusText}`,
          ...failureMessage(data),
          details: data?.details,
          status: response.status,
          ...refusalFields(data),
        }
      }

      // Handle backend response format: { success: true, data: ... }
      if (data && typeof data === 'object' && 'success' in data) {
        if (data.success) {
          return {
            success: true,
            data: data.data,
            status: response.status,
            ...membershipFields(data),
            ...warningFields(data),
          }
        } else {
          return {
            success: false,
            error: data.error || 'Backend error',
            ...failureMessage(data),
            details: data.details,
            status: response.status,
            ...refusalFields(data),
          }
        }
      }

      // Fallback for direct data responses
      return {
        success: true,
        data,
        status: response.status,
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      }
    }
  }

  async get<T>(endpoint: string, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET', headers: customHeaders })
  }

  async post<T>(
    endpoint: string,
    body?: any,
    customHeaders?: Record<string, string>,
    init?: Pick<RequestInit, 'keepalive'>
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...init,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers: customHeaders,
    })
  }

  async put<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }
}

// Create API client instance
console.log('🌐 API Base URL:', API_BASE_URL)
const apiClient = new ApiClient(API_BASE_URL)

// Account management
export const accountsApi = {
  create: (data: CreateAccountRequest) => apiClient.post('/accounts', data),
  list: () => apiClient.get('/accounts'),
  get: (id: string) => apiClient.get(`/accounts/${id}`),
}

// Workspace management
export const workspacesApi = {
  create: (data: CreateWorkspaceRequest) => apiClient.post('/workspaces', data),
  list: () => apiClient.get('/workspaces'),
  get: (id: string) => apiClient.get(`/workspaces/${id}`),
  update: (id: string, data: Partial<CreateWorkspaceRequest>) =>
    apiClient.put(`/workspaces/${id}`, data),
  getEmailPreviewCart: (id: string): Promise<ApiResponse<EmailPreviewCart>> =>
    apiClient.get(`/workspaces/${id}/email-preview-cart`),
  previewEmailStyle: (id: string, emailStyles: unknown, offer = false, cartId?: string): Promise<ApiResponse<{ htmlBody: string }>> =>
    apiClient.post(`/workspaces/${id}/email-style-preview`, { emailStyles, offer, cartId }),
  uploadEmailLogo: (id: string, data: EmailLogoUploadRequest) =>
    apiClient.post(`/workspaces/${id}/email-logo`, data),
  /** MAR-391: the STORED Shopify look — no detection run, no write. The overview reads this. */
  getEmailBrandDetection: (id: string): Promise<ApiResponse<EmailBrandLookRead>> =>
    apiClient.get(`/workspaces/${id}/email-brand-detection`),
  refreshEmailBrandDetection: (id: string): Promise<ApiResponse<EmailBrandDetectionReview>> =>
    apiClient.post(`/workspaces/${id}/email-brand-detection`),
  confirmEmailBrandDetection: (id: string, data: EmailBrandDetectionConfirmation) =>
    apiClient.post(`/workspaces/${id}/email-brand-detection/confirm`, data),
  getAttribution: (id: string, period?: string): Promise<ApiResponse<AttributionData>> =>
    apiClient.get(`/workspaces/${id}/attribution${period ? `?period=${period}` : ''}`),

  // Shadow mode endpoints
  getShadowStats: (id: string, period?: string): Promise<ApiResponse<DashboardStats>> =>
    apiClient.get(`/workspaces/${id}/shadow/stats${period ? `?period=${period}` : ''}`),
  getShadowUsage: (id: string): Promise<ApiResponse<DashboardUsage>> =>
    apiClient.get(`/workspaces/${id}/shadow/usage`),
  getShadowDecisions: (
    id: string,
    params?: { type?: string; status?: string; limit?: number; startKey?: string }
  ): Promise<ApiResponse<ShadowDecisionsResponse>> => {
    const searchParams = new URLSearchParams()
    if (params?.type) searchParams.set('type', params.type)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.startKey) searchParams.set('startKey', params.startKey)
    const query = searchParams.toString()
    return apiClient.get(`/workspaces/${id}/shadow/decisions${query ? `?${query}` : ''}`)
  },
  getShadowDecision: (id: string, decisionId: string): Promise<ApiResponse<ShadowDecision>> =>
    apiClient.get(`/workspaces/${id}/shadow/decisions/${decisionId}`),
  submitShadowFeedback: (
    id: string,
    decisionId: string,
    data: { approved: boolean; reason?: string }
  ): Promise<ApiResponse<{ decisionId: string; approved: boolean; reason?: string }>> =>
    apiClient.post(`/workspaces/${id}/shadow/decisions/${decisionId}/feedback`, data),
}

export interface EmailPreviewCartItem { productId: string; title?: string; quantity?: number; price?: number; imageUrl?: string }
export interface EmailPreviewCart { cartId: string; cartToken: string; abandonedAt?: string | number; currency?: string; total?: number; items: EmailPreviewCartItem[] }
export interface EmailLogoUploadRequest {
  pngBase64: string; pixelWidth: number; pixelHeight: 80
}

export type EmailBrandFactTag = 'shopify' | 'adjusted' | 'default' | 'chosen'
export interface EmailBrandFactProvenance { tag: EmailBrandFactTag; reason: string }
export interface EmailBrandLogoFact { url: string; mimeType: 'image/png'; pixelWidth: number; pixelHeight: 80 }
export interface EmailBrandFacts {
  brandColor: string
  typeface: 'sans' | 'serif'
  corners: 'square' | 'soft' | 'round'
  paper: 'warm' | 'white'
  logo: EmailBrandLogoFact | null
}
export interface EmailBrandSuggestion {
  id: string
  detectedAt: string
  baselineStylesFingerprint?: string
  baselineWorkspaceName?: string | null
  observedShopName?: string | null
  facts: EmailBrandFacts
  logoCandidate?: { url: string; sourceWidth?: number; sourceHeight?: number } | null
  provenance: Record<keyof EmailBrandFacts, EmailBrandFactProvenance>
}
export interface EmailBrandDetectionReview {
  state: 'proposed' | 'current' | 'unavailable'
  suggestion?: EmailBrandSuggestion
  reason?: string
}
/**
 * The stored Shopify observation, read without running detection (MAR-391). `stale` is a stored
 * proposal the live styles or name have moved past — the next detection replaces it, so it is
 * not something to confirm. `unavailable` here only ever means there is no shop (`invalid_shop`).
 * The proposal itself is not returned — the refresh returns it when How we look opens.
 */
export interface EmailBrandLookRead {
  state: 'unread' | 'proposed' | 'stale' | 'current' | 'unavailable'
  observedAt?: string
  reason?: string
}
export interface EmailBrandDetectionConfirmation {
  suggestionId: string
  emailStyles: EmailBrandFacts
  useShopifyName?: boolean
}

// Attribution data types
export interface AttributionData {
  period?: string;
  summary: {
    revenue: {
      '24h': { total: number; count: number };
      '7d': { total: number; count: number };
      '30d': { total: number; count: number };
      '90d'?: { total: number; count: number };
      '180d'?: { total: number; count: number };
      '365d'?: { total: number; count: number };
      [key: string]: { total: number; count: number } | undefined;
    };
    totalAttributions: number;
  };
  channelBreakdown: {
    email: { revenue: number; count: number; conversions: number };
    chat: { revenue: number; count: number; conversions: number };
    engagement: { revenue: number; count: number; conversions: number };
  };
  /**
   * The period's attributed orders grouped by currency, largest revenue first. ⚠️ Read THIS for
   * a revenue figure, never `summary.revenue[period].total`, which adds currencies together.
   * `currency: 'unknown'` is a row that stored none — no currency is invented for it.
   */
  byCurrency: Array<{
    currency: string;
    revenue: number;
    count: number;
    channels?: Record<string, { revenue: number; count: number; conversions: number }>;
  }>;
  /**
   * The partition held more than the server's row cap. It is read newest first, so the OLDEST
   * orders were dropped: a long window that hits the cap is a lower bound, not a total.
   */
  truncated?: boolean;
  /**
   * The store's own order revenue for the SAME period, per currency (MAR-392) — the denominator
   * of the Ledger's "Share of revenue". Counted as attribution counts: every order placed in the
   * window, gross at order time, cancelled ones included, refunds not subtracted.
   * `null` with `storeRevenueUnavailable: true` is a failed read — never zeros. ⚠️ `truncated`
   * (`stoppedBy` row_cap | deadline) means the sum is PARTIAL: never divide by it.
   */
  storeRevenue?: {
    period: string;
    byCurrency: Array<{ currency: string; revenue: number; count: number }>;
    nonSaleOrders?: { cancelled: number; voided: number; expired: number };
    truncated: boolean;
    stoppedBy: 'end_of_window' | 'row_cap' | 'deadline';
    definition?: string;
  } | null;
  storeRevenueUnavailable?: boolean;
  recentOrders: Array<{
    orderId: string;
    orderNumber: string;
    orderTotal: number;
    currency: string;
    attributionType: string;
    confidence: string;
    touchpointType: string;
    touchpointCount: number;
    createdAt: string;
  }>;
  model: {
    learnedAt: string;
    sampleCount: number;
    windows: Record<string, number>;
    influenceWeights: {
      firstTouch: number;
      lastTouch: number;
      middleTouches: number;
    };
    stats: Record<string, unknown>;
  } | null;
}

// Trigger breakdown for shadow stats
export interface TriggerBreakdown {
  count: number
  projectedRecovery: number
  probability: number
}

// User management
export const usersApi = {
  create: (data: CreateUserRequest) => apiClient.post('/users', data),
  list: () => apiClient.get('/users'),
  get: (id: string) => apiClient.get(`/users/${id}`),
  getMemberships: (clerkUserId: string): Promise<ApiResponse<UserMembershipsResponse>> =>
    apiClient.get(`/users/memberships/${clerkUserId}`),
  listByWorkspace: (workspaceId: string): Promise<ApiResponse<WorkspaceUser[]>> =>
    apiClient.get(`/users/workspace/${workspaceId}`),
  updateRole: (id: string, role: 'owner' | 'admin' | 'member'): Promise<ApiResponse<WorkspaceUser>> =>
    apiClient.put(`/users/${id}`, { role }),
  updateProfile: (id: string, data: { firstName?: string; lastName?: string; name?: string }): Promise<ApiResponse<WorkspaceUser>> =>
    apiClient.put(`/users/${id}`, data),
  invite: (data: InviteUserRequest): Promise<ApiResponse<WorkspaceUser>> =>
    apiClient.post('/users/invite', data),
  remove: (id: string): Promise<ApiResponse<void>> =>
    apiClient.delete(`/users/${id}`),
  linkInvitation: (email: string, clerkUserId: string, firstName?: string | null, lastName?: string | null): Promise<ApiResponse<{ linked: number }>> =>
    apiClient.post('/users/link-invitation', { email, clerkUserId, firstName, lastName }),
}

// Shopify synchronization
export const syncApi = {
  customers: (data: SyncRequest) =>
    apiClient.post('/shopify/sync-customers', data),
  products: (data: SyncRequest) =>
    apiClient.post('/shopify/sync-products', data),
  orders: (data: SyncRequest) => apiClient.post('/shopify/sync-orders', data),
}

// Webhook management
export const webhooksApi = {
  setup: (data: { accountId: string; workspaceId: string; action?: 'setup' | 'delete' }) =>
    apiClient.post('/shopify/webhook-setup', data),
}

// Email Domain Verification Types
export interface DnsRecord {
  type: 'CNAME' | 'TXT' | 'MX';
  name: string;
  value: string;
  purpose: 'dkim' | 'spf' | 'dmarc' | 'verification';
  required: boolean;
  status?: 'pending' | 'verified' | 'missing';
}

export interface EmailDomainConfig {
  workspaceId: string;
  domain: string;
  rootDomain: string;
  useSubdomain: boolean;
  verificationStatus: 'pending' | 'verified' | 'failed' | 'expired';
  verificationStartedAt?: string;
  verifiedAt?: string;
  lastCheckedAt?: string;
  dkimStatus: 'pending' | 'success' | 'failed';
  dkimTokens?: string[];
  fromName: string;
  fromLocalPart: string;
  fromAddress: string;
  replyToAddress?: string;
  failureReason?: string;
  createdAt: string;
  updatedAt: string;
  dnsRecords?: DnsRecord[];
}

export interface InitiateDomainRequest {
  domain: string;
  useSubdomain?: boolean;
  fromName: string;
  fromLocalPart?: string;
}

export interface InitiateDomainResponse {
  domain: string;
  verificationStatus: string;
  dnsRecords: DnsRecord[];
  fromAddress: string;
  message: string;
}

export interface UpdateFromAddressRequest {
  fromName?: string;
  fromLocalPart?: string;
  replyToAddress?: string;
}

export interface DnsRecordsResponse {
  domain: string;
  records: DnsRecord[];
  instructions: {
    cloudflare: string;
    godaddy: string;
    namecheap: string;
    route53: string;
  };
}

// Email Domain Verification API
export const emailDomainApi = {
  // Initiate domain setup
  initiate: (workspaceId: string, data: InitiateDomainRequest): Promise<ApiResponse<InitiateDomainResponse>> =>
    apiClient.post(`/workspaces/${workspaceId}/email/domain`, data),

  // Get domain configuration and status
  get: (workspaceId: string): Promise<ApiResponse<EmailDomainConfig>> =>
    apiClient.get(`/workspaces/${workspaceId}/email/domain`),

  // Remove domain
  remove: (workspaceId: string): Promise<ApiResponse<{ message: string; domain: string }>> =>
    apiClient.delete(`/workspaces/${workspaceId}/email/domain`),

  // Trigger manual verification check
  verify: (workspaceId: string): Promise<ApiResponse<{ verificationStatus: string; dkimStatus: string; lastCheckedAt: string }>> =>
    apiClient.post(`/workspaces/${workspaceId}/email/domain/verify`),

  // Get DNS records
  getDnsRecords: (workspaceId: string): Promise<ApiResponse<DnsRecordsResponse>> =>
    apiClient.get(`/workspaces/${workspaceId}/email/domain/dns-records`),

  // Update from address
  updateFromAddress: (workspaceId: string, data: UpdateFromAddressRequest): Promise<ApiResponse<{ fromName: string; fromLocalPart: string; fromAddress: string; replyToAddress?: string }>> =>
    apiClient.put(`/workspaces/${workspaceId}/email/from-address`, data),
}

// Data queries
export const dataApi = {
  query: (sql: string, params?: Record<string, any>) =>
    apiClient.post('/data/query', { sql, params }),
  customers: (accountId: string, workspaceId: string) =>
    apiClient.get(`/data/customers?accountId=${accountId}&workspaceId=${workspaceId}`),
  products: (accountId: string, workspaceId: string) =>
    apiClient.get(`/data/products?accountId=${accountId}&workspaceId=${workspaceId}`),
  orders: (accountId: string, workspaceId: string) =>
    apiClient.get(`/data/orders?accountId=${accountId}&workspaceId=${workspaceId}`),
  analytics: (accountId: string, workspaceId: string, timeframe: string) =>
    apiClient.get(`/data/analytics?accountId=${accountId}&workspaceId=${workspaceId}&timeframe=${timeframe}`),
}

// Embed script generation
export const embedApi = {
  generateScript: (accountId: string, workspaceId: string) =>
    apiClient.get(`/embed-script/${accountId}/${workspaceId}`),
}

// System status
export const systemApi = {
  health: () => apiClient.get('/health'),
  status: () => apiClient.get('/status'),
}

// OAuth API for Shopify Partner App flow
export interface OAuthInstallResponse {
  authorizationUrl: string;
  shop: string;
  state: string;
  scopes: string[];
}

export const oauthApi = {
  // PUBLIC install (no Marva session — ConnectPage): a brand-new merchant, or a reinstall the
  // callback resolves by shop domain. It takes no account or workspace: the server refuses
  // them, because a query parameter is not an identity (MAR-276).
  getInstallUrl: (shop: string): Promise<ApiResponse<OAuthInstallResponse>> => {
    const params = new URLSearchParams({ shop });
    return apiClient.get(`/oauth/install?${params.toString()}`);
  },
  // AUTHENTICATED install: connect a store to an existing workspace (owner/admin only), or —
  // with no workspaceId — into the signed-in caller's own account. The server derives the
  // account from the session/workspace; nothing here names one.
  getConnectUrl: (shop: string, workspaceId?: string): Promise<ApiResponse<OAuthInstallResponse>> => {
    const params = new URLSearchParams({ shop });
    if (workspaceId) {
      params.set('workspaceId', workspaceId);
    }
    return apiClient.get(`/oauth/connect?${params.toString()}`);
  },
}

// Export the client for custom requests
export { apiClient }

// Chat API
export interface ToolOutput {
  type: 'chart' | 'table' | 'metric_card' | 'text'
  title?: string
  // Chart-specific
  chartType?: 'line' | 'bar' | 'area' | 'pie' | 'composed'
  data?: {
    labels: string[]
    /**
     * ⚠️ `null` is a MISSING bucket, not a zero (merchant-chat `salesChart`): a
     * currency whose series does not carry a bucket reports nothing for it, and
     * the chart draws a gap there rather than a sale of 0.
     */
    datasets: Array<{
      label: string
      data: Array<number | null>
      color?: string
      /** Per point, aligned with `data`: this point's bucket was cut short by the window. */
      partial?: boolean[]
    }>
    /**
     * Per label, aligned with `labels`: a bucket the window only partly covers
     * (the first and last of a report usually are). Applies to every dataset.
     */
    partial?: boolean[]
  }
  options?: {
    showLegend?: boolean
    stacked?: boolean
    yAxisLabel?: string
    xAxisLabel?: string
    valuePrefix?: string
    valueSuffix?: string
  }
  // Table-specific
  columns?: Array<{ key: string; label: string }>
  rows?: Array<Record<string, unknown>>
  // Metric card-specific
  value?: string
  change?: number
  trend?: 'up' | 'down' | 'flat'
  // Text-specific
  content?: string
}

/**
 * A source card (MAR-173 PR 3): what one capability call read, built by
 * merchant-chat's CODE from the tool result — never parsed from model text.
 * Mirrors `toSourceCard` in `services/chat/merchant-chat/src/capabilities/registry.js`,
 * which is the source of truth.
 *
 * ⚠️ `deepLink` is data from the server, and the client still refuses any route
 * not in its own copy of the allowlist (`components/chat/SourceCard.tsx`).
 */
export type SourceCardStatus =
  | 'ok'
  | 'partial'
  | 'unavailable'
  | 'not_found'
  | 'invalid'
  | 'refused'
  | 'cancelled'

export interface SourceCard {
  /** The registry tool name, e.g. `explain_decision`. */
  capability: string
  kind: string
  /** Absent on `not_found` and on surface-level cards. */
  recordId?: string
  title: string
  deepLink: string
  status: SourceCardStatus
  output?: ToolOutput
  /** A stored card whose output was too large to keep on the history row. */
  outputDropped?: boolean
}

/**
 * An EARLIER read that was in front of Marva when she answered (MAR-173
 * replay): the card a replayed call from the previous turn produced, plus
 * `readAt` — when that read actually happened (ISO). It is NOT a claim that
 * the reply used it. It is never one of the answer's own `sources`, and must
 * never be drawn as one.
 */
export interface PriorSourceCard extends SourceCard {
  readAt: string
}

export interface ChatResponse {
  message: string
  conversationId: string
  /**
   * In call order, deduped. Optional on the type because a reply that read
   * nothing may omit it; there is deliberately no `toolOutputs` here any more —
   * only a reloaded pre-cutover turn carries those (`ConversationMessage`).
   */
  sources?: SourceCard[]
  /**
   * The earlier reads that were in front of Marva when she answered: on any
   * answered turn that read nothing itself and was sent the previous turn's
   * replayed results — a "thanks" included, because the server cannot tell
   * whether the reply used them. Context, not a claim. Absent on a turn that
   * read anything, on a guardrail, too-long, model-error or no-time turn, and
   * when no replay was sent.
   */
  priorSources?: PriorSourceCard[]
  /**
   * A change Marva PREPARED this turn, never applied (MAR-175). Present only when an
   * owner|admin turn's write-preparation tool prepared one. It carries the one-time
   * confirm `token` — `useAskThread` keeps that in memory only and strips it from the
   * transcript. A chat message confirms nothing; only `governanceApi.confirmProposal` does.
   */
  proposal?: Proposal
  /** Why the turn ended, and whether `message` is the model's answer or a fixed fallback (MAR-299). */
  outcome?: MerchantAskOutcome
  answered?: boolean
}

/**
 * The exact before/after of a prepared change. Mirrors the receipt shapes in
 * `@marva/shared/schemas` (`MerchantActionAuthorityStateSchema`, `…AnswerBefore/AfterSchema`).
 */
export type ProposalCapability = 'authority_change' | 'customer_answer'
export interface ProposalAuthorityState {
  rung: AuthorityConfig['rung']
  exceptions: AuthorityException[]
}
export interface ProposalAnswerBefore {
  notificationId: string
  status: 'pending'
}
export interface ProposalAnswerAfter {
  answerText: string
}
/**
 * What a card shows a person but the record does not keep. The customer's question
 * comes back on the `POST /chat/merchant` response only — **transient, never stored**
 * — so a reloaded `customer_answer` proposal has no question to show.
 */
export interface ProposalDisplay {
  question: string
}

/**
 * A proposal as a thread STORES it (`GET /chat/merchant/{id}`): everything but the token,
 * which the server never keeps in a thread. A reloaded one can therefore only be shown,
 * never confirmed.
 *
 * ⚠️ A stored `customer_answer` carries **no `before`, `after` or `display`** — only the
 * summary — so both are optional here. `display` is present only on the in-memory copy
 * of the turn that prepared it (`withoutToken` keeps it; the server never sends it back).
 */
export interface StoredProposal {
  /** `act_` + 24 hex — the Ledger's id for the receipt. */
  receiptId: string
  capability: ProposalCapability
  summary: string
  before?: ProposalAuthorityState | ProposalAnswerBefore | null
  after?: ProposalAuthorityState | ProposalAnswerAfter
  display?: ProposalDisplay
  /** ISO; ten minutes after preparation. */
  expiresAt: string
}

/**
 * A live proposal from `POST /chat/merchant`: the one-time token, the exact
 * before/after, and — for `customer_answer` — the question to show beside the answer.
 */
export interface Proposal extends StoredProposal {
  /** Opaque, 43 chars base64url. Returned once; never persisted anywhere by the client. */
  token: string
  before: ProposalAuthorityState | ProposalAnswerBefore | null
  after: ProposalAuthorityState | ProposalAnswerAfter
}

/**
 * The receipt the proposal's row becomes (`publicReceipt` in
 * `@marva/shared/merchant-action-receipts`). Only the fields the client reads are typed.
 */
export interface MerchantActionReceipt {
  receiptId: string
  capability: ProposalCapability
  status: 'prepared' | 'applying' | 'applied' | 'cancelled' | 'expired' | 'superseded' | 'failed'
  summary: string
  before: ProposalAuthorityState | ProposalAnswerBefore | null
  after: ProposalAuthorityState | ProposalAnswerAfter
  preparedAt: string
  expiresAt: string
  appliedAt?: string
  settledAt?: string
  result?: { returnStatus?: string; mode?: 'shadow' | 'live' }
}

/**
 * The Ask context hint — what the merchant was looking at when they asked
 * (MAR-174 §3.2). It mirrors `AskContextHintSchema` in `@marva/shared/schemas`,
 * which is **strict**: a key not declared here is a 400, not a silent drop.
 *
 * ⚠️ It is deliberately NOT the shell's `AskEntity`, and the two must not be
 * conflated. `AskEntity` carries `label` (display text — `custName` falls back
 * to a customer's email address) and `workspaceId` (the server derives the
 * workspace from the authorizer). Neither belongs on the wire, so one
 * converter — `shell/askContext.ts` `toContextHint` — owns the narrowing, and
 * nothing else builds one of these.
 *
 * A hint is a hint. It scopes retrieval and improves tool choice; it grants
 * nothing, and the server drops any entity it cannot prove this workspace owns.
 */
export interface AskContextHint {
  surface: 'ledger' | 'knowledge' | 'authority' | 'value'
  entity?: {
    type: 'decision' | 'customer' | 'product' | 'authority_exception' | 'attribution_order'
    id: string
  }
  view?: {
    window?: '24h' | '7d' | '30d' | '365d'
    filters?: Record<string, string>
  }
}

// A chat thread summary for the Ask "Recent" rail
export interface ConversationSummary {
  conversationId: string
  title: string
  preview: string
  createdAt: string
  updatedAt: string
}

// A persisted message as returned when loading a thread
export interface ConversationMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  /** Source cards for an assistant turn written after MAR-173 PR 3. */
  sources?: SourceCard[]
  /** Earlier reads that were in front of Marva when she answered (see `PriorSourceCard`). */
  priorSources?: PriorSourceCard[]
  /** Legacy: only turns written before MAR-173 PR 3 carry this. */
  toolOutputs?: ToolOutput[]
  /** The proposal this turn prepared, stored WITHOUT its token — shown, never confirmable. */
  proposal?: StoredProposal
}

export interface ConversationDetail {
  conversationId: string
  title: string
  messages: ConversationMessage[]
}

export const chatApi = {
  sendMessage: (data: {
    message: string
    workspaceId: string
    accountId?: string
    conversationId?: string
    /** Built only by `shell/askContext.ts` `toContextHint`. See `AskContextHint`. */
    context?: AskContextHint
  }) => apiClient.post<ChatResponse>('/chat/merchant', data),

  // List a workspace's chat threads, newest first
  listConversations: (workspaceId: string): Promise<ApiResponse<{ conversations: ConversationSummary[] }>> =>
    apiClient.get(`/chat/merchant?workspaceId=${encodeURIComponent(workspaceId)}`),

  // Load one thread's full message history
  getConversation: (workspaceId: string, conversationId: string): Promise<ApiResponse<ConversationDetail>> =>
    apiClient.get(`/chat/merchant/${encodeURIComponent(conversationId)}?workspaceId=${encodeURIComponent(workspaceId)}`),
}

// Governance API
export interface BrandVoice {
  tone: 'friendly' | 'professional' | 'casual' | 'luxurious' | 'playful' | 'custom'
  /**
   * The merchant's own words for their tone, and the only thing `tone: 'custom'` means.
   *
   * ⚠️ Optional on the wire and REMOVED by the PUT whenever it is absent, so a tone the
   * merchant abandoned cannot leave its prose standing (`BRAND_VOICE_FIELDS` in
   * `services/governance/governance-api/src/index.js`). A caller sending `tone: 'custom'`
   * without this field is asking `buildBrandVoiceInstructions` to fall back to `balanced` —
   * a voice neither party chose.
   */
  customToneDescription?: string
  formalityLevel: number
  emojiUsage: 'never' | 'sparingly' | 'frequently'
  responseLength: 'concise' | 'balanced' | 'detailed'
  forbiddenWords: string[]
  requiredPhrases: {
    greeting?: string
    signOff?: string
  }
  exampleResponses: Array<{
    context: string
    response: string
  }>
}

/**
 * ── The derived voice (MAR-106 backend, MAR-108 PR 3 is its first caller) ──────────────
 *
 * `BrandVoice` above is what the merchant STATES. This is what Marva worked out by reading
 * the storefront, and it reaches no consumer until the merchant has confirmed it — the gate
 * is `getBrandVoice` in `@marva/shared/governance`, not anything here.
 *
 * ⚠️ These types mirror `BrandVoiceDerivedSchema` in `packages/shared/src/schemas.js`. That
 * schema is the contract; this is a projection of it for one screen, and where they disagree
 * the schema is right.
 */

/** The five the deriver may propose. `custom` is NOT among them — it is a pointer at
 *  `customToneDescription`, which is merchant-stated and lives in the legacy form. */
export type DerivedTone = Exclude<BrandVoice['tone'], 'custom'>

/** The four traits the card shows and "Correct her" edits. `summary` is prose, not a trait. */
export interface CorrectableTraits {
  tone: DerivedTone
  formalityLevel: number
  emojiUsage: BrandVoice['emojiUsage']
  responseLength: BrandVoice['responseLength']
}

export interface DerivedVoiceTraits extends CorrectableTraits {
  summary: string
}

export interface DerivedVoiceProvenance {
  /** The registrable domain actually read after redirects — what the merchant would recognise,
   *  not the `*.myshopify.com` the fetch started from. */
  sourceDomain: string
  /** Identifies the proposal. Confirming or correcting sends the one that was RENDERED. */
  derivedAt: string
  pageCount: number
  modelId: string
  confidence: number
}

export interface DerivedVoice {
  traits: DerivedVoiceTraits
  provenance: DerivedVoiceProvenance
  /** ⚠️ Optional on the wire: `BrandVoiceDerivedSchema.status` carries `.default('proposed')`,
   *  so a legacy block can be stored without one. Only the literal `confirmed` is confirmation. */
  status?: 'proposed' | 'confirmed'
  confirmedAt?: string | null
  confirmedBy?: string | null
}

/** Every field a merchant can state — the key space of `fieldSources`. */
export type BrandVoiceField =
  | 'tone' | 'customToneDescription' | 'formalityLevel' | 'forbiddenWords'
  | 'requiredPhrases' | 'exampleResponses' | 'emojiUsage' | 'responseLength'

/** Per-field provenance. `setBy: 'merchant'` is what a confirmation supersedes. */
export interface BrandVoiceFieldSource {
  setBy: 'auto' | 'merchant'
  merchantOverride?: boolean
  confidence?: number
  updatedAt?: string
}

export type VoiceState = 'idle' | 'deriving' | 'proposed' | 'confirmed'

export interface VoiceReview {
  workspaceId: string
  configType: 'brand-voice'
  /** ⚠️ Render from THIS. Never infer a state client-side — the server owns the in-flight
   *  window, and a card that computed its own would disagree with the button it offers. */
  state: VoiceState
  /** ⚠️ The RAW derived block, not the gated read: this route is the review itself, and a
   *  proposal is unconfirmed by definition until the merchant answers it. */
  derived: DerivedVoice | null
  fieldSources: Partial<Record<BrandVoiceField, BrandVoiceFieldSource>>
  /** Returned raw, so the card can say a past attempt did not finish. */
  derivationRequest: { requestedAt: string; requestedBy?: string | null } | null
  /** On the derive 202 only: `false` means someone else's run is already in flight. */
  accepted?: boolean
  reason?: string
}

// `sale_launch` / `sale_reminder` are gone: O1 item 3 phase 5 deleted the sale campaign,
// so there is no send to preview. A sale now reaches a shopper only inside a moment that
// was already happening for its own reasons.
export type EmailTestType =
  | 'cart_recovery'
  | 'browse_followup'
  | 'comparison'
  | 'inspiration'
  | 'back_in_stock'
  | 'price_drop'

export interface TestEmailRequest {
  emailType: EmailTestType
  recipientEmail: string
  productIds?: string[]
  cartId?: string
  previewOffer?: boolean
  customerId?: string
  context?: {
    customerFirstName?: string
    oldPrice?: number
    newPrice?: number
  }
}

export interface TestEmailResponse {
  emailId: string
  subject: string
  bodyPreview: string
  sentTo: string
  sentAt: string
  emailType: EmailTestType
}

export type SmsTestType =
  | 'cart_recovery'
  | 'back_in_stock'
  | 'price_drop'
  | 'browse_followup'

export interface TestSmsRequest {
  smsType: SmsTestType
  recipientPhone: string
  productIds: string[]
  customerId?: string
  context?: {
    customerFirstName?: string
  }
}

export interface TestSmsResponse {
  success: boolean
  message: string
  conversationId: string
  sentTo: string
  sentAt: string
}

export interface QuietWindow {
  start: number
  end: number
}

// Engagement rules (Governance V2). Numeric caps may be null → "use the store archetype default".
export interface EngagementRules {
  timezone?: string | null
  quietHours: {
    global: QuietWindow
    // Email only (MAR-159): an SMS's window is `smsConfig`, and on-site chat has none.
    overrides: { email: QuietWindow | null }
  }
  frequencyCaps: {
    email: { perWeek: number | null; perMonth: number | null }
    sms: { perMonth: number | null }
    onSite: { perSession: number }
  }
  cooldown: { minGapHours: number | null }
  fatigue: {
    enabled: boolean
    perChannel: { email: number; sms: number; onSite: number }
    lookbackDays: number
  }
  blackoutDates: string[]
  boostDates: string[]
}

// Notification preferences (Governance V2 Phase 4) — per-mode × per-type delivery matrix.
export interface PerTypePref {
  enabled: boolean
  delivery: 'immediate' | 'aggregated'
  cadence: 'hourly' | 'daily' | 'weekly'
  sendHourLocal: number
  sendDayLocal: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'
  channels: Array<'dashboard' | 'email' | 'slack'>
}

export interface NotificationPrefs {
  timezone?: string | null
  shadow: Partial<Record<'digest_item' | 'alert' | 'escalation', Partial<PerTypePref>>>
  assisted: Partial<Record<'digest_item' | 'alert' | 'escalation', Partial<PerTypePref>>>
  slackWebhookUrl?: string | null
  emailRecipients: string[]
}

export interface EscalationItem {
  notificationId: string
  createdAt: string
  title: string
  summary?: string
  status: 'pending' | 'approved' | 'rejected' | 'expired'
  triggerType?: string
  triggerValue?: number
  threshold?: number
  customerRef?: string | null
  expiresAt?: string
  payload?: Record<string, unknown>
}

export interface QuestionItem {
  notificationId: string
  createdAt: string
  question: string
  suggestedAnswer?: string | null
  answerText?: string | null
  status: 'pending' | 'answered' | 'declined' | 'expired'
  customerRef?: string | null
  expiresAt?: string
}

export interface MerchantAnswerItem {
  fingerprint: string
  question: string
  answerText: string
  answeredBy?: string | null
  source: 'ask_merchant' | 'manual'
  expiresAt?: string | null
  createdAt: string
  updatedAt: string
}

/** The rung and its downward exceptions (D7 — authority as data). */
export interface AuthorityException {
  actionClass: string
  /** Phase 1 has one kind: the class is refused, silently. Bounds arrive with escalation. */
  kind: 'off'
  grantedVia: 'ui' | 'receipt' | 'migration'
  expiresAt: string | null
  createdAt?: string
  note?: string | null
}

export interface AuthorityConfig {
  rung: 'shadow' | 'assisted' | 'autonomous'
  /** Stored only while `rung` is Shadow: the live authority restored on activation. */
  savedRung?: 'assisted' | 'autonomous'
  /** The live boundary saved for activation, projected by the server. */
  configuredRung?: 'assisted' | 'autonomous' | null
  /** What is acting now after workspace mode is applied. */
  effectiveRung?: 'shadow' | 'assisted' | 'autonomous' | null
  /** The rung activation will restore; explicit for rowless/legacy Shadow rows. */
  activationRung?: 'assisted' | 'autonomous' | null
  /** True only for a legacy live+stored-Shadow pairing that needs repair. */
  authorityStateMismatch?: boolean
  /** True when workspace mode, rather than the stored rung, is holding sends. */
  heldByMode?: boolean
  exceptions: AuthorityException[]
  /**
   * GET only: what she does above the discount ceiling, in the merchant's voice, for the live
   * `AUTHORITY_ESCALATION_ENABLED` (MAR-300). The server says it because only the server knows
   * the flag; render it as sent, and say nothing when it is absent.
   */
  aboveCeiling?: string
  migratedAt?: string | null
  updatedAt?: string
  /**
   * Server-derived workspace safety mode. Both GET and PUT carry it; `AuthorityPanel` also
   * pushes a successful PUT's value into the workspace store immediately. NEVER sent:
   * `saveAuthority` constructs a persisted-field-only body and the API answers 400 to `mode`.
   */
  mode?: 'shadow' | 'live' | 'disabled' | null

  /**
   * Optimistic concurrency on the authority row — **write-only**, never returned.
   *
   * ⚠️ The server's transaction conditions on `workspace.mode`, not on this row, so two
   * same-mode writers each get a 200 and the later silently discards the earlier. Both editing
   * paths submit the whole document they read, so that is a lost update, not a merge: two tabs
   * lifting different exceptions reinstate one of them while both report success.
   *
   * `null` asserts "there was no row when I read"; a string asserts "the row I read had this
   * `updatedAt`". Omitting it entirely keeps the old unconditional behaviour, which is what
   * blind operator writes and the migration want.
   */
  expectedUpdatedAt?: string | null

  /**
   * The stored row could not be parsed. ⚠️ **Read-only, and it is not a rung.**
   *
   * The enforcer answers the same bytes with the lowest rung and `readError: true`, refusing
   * every gated class. The GET reports the failure rather than serving a default, so the screen
   * says it does not know instead of making an authoritative claim about a state the send path
   * treats as unreadable. Repair is an operator action.
   */
  readError?: boolean

  /**
   * There is no stored row, and `rung` was derived from `workspace.mode`.
   *
   * The pre-migration state: phase 3 has not cut over, so a workspace can sit at
   * `mode: 'shadow'` with no authority row. The response carries no `updatedAt`, so the first
   * deliberate write sends `expectedUpdatedAt: null` and creates the row conditionally.
   */
  derivedFromMode?: boolean

  /**
   * The workspace is in the admin-only `disabled` mode. ⚠️ **Not a rung, and not Shadow.**
   *
   * Since MAR-24 a disabled workspace sends NOTHING (`@marva/shared/send-hold`), but it is not
   * a preview either: its held decisions are recorded `suppressed` (`workspace_disabled`), not
   * `shadowed`, and only Marva admins can change it. It is surfaced as its own read-only state.
   *
   * ⚠️ **Orthogonal to the row**, so it can arrive with ANY outcome — a valid stored row, a
   * derived one, or a read failure. Handling it on only one branch is what made it "disabled,
   * but only until your first PUT"; `AuthorityPanel` therefore keeps it on the snapshot rather
   * than on the parsed config.
   */
  workspaceDisabled?: boolean
}

export const governanceApi = {
  // ── Confirmed writes from Ask (MAR-175) ──────────────────────────────────────────────
  //
  // The ONLY way a proposal Marva prepared is applied: a person pressing Confirm. 200
  // `{ receipt, replayed? }` — a replay answers the original receipt. Refusals carry
  // `details.reason` (`confirm_requires_admin`, `proposal_actor_mismatch`,
  // `proposal_superseded`, `proposal_expired`, `writes_disabled`, …).
  confirmProposal: (
    workspaceId: string,
    token: string
  ): Promise<ApiResponse<{ receipt: MerchantActionReceipt; replayed?: boolean }>> =>
    apiClient.post(
      `/workspaces/${encodeURIComponent(workspaceId)}/proposals/${encodeURIComponent(token)}/confirm`
    ),
  cancelProposal: (
    workspaceId: string,
    token: string
  ): Promise<ApiResponse<{ receipt: MerchantActionReceipt; replayed?: boolean }>> =>
    apiClient.post(
      `/workspaces/${encodeURIComponent(workspaceId)}/proposals/${encodeURIComponent(token)}/cancel`
    ),

  // ── Authority (O1 item 6) ─────────────────────────────────────────────────────────────
  //
  // This is the row `getAuthorityConfig` in `@marva/shared/governance-enforcer` reads on the
  // send path, so the screen and the gate cannot disagree about the rung.
  getAuthority: (workspaceId: string, client = apiClient): Promise<ApiResponse<AuthorityConfig>> =>
    client.get(`/workspaces/${workspaceId}/governance/authority`),

  /**
   * Only persisted merchant-owned fields cross the write boundary. Effective/configured state is
   * server projection and must never be echoed back as input.
   *
   * `workspace.mode` is Marva-admin-only after a 2026-07 audit; the rung mirrors onto it
   * SERVER-side, and the handler answers 400 to a body carrying it. The old ladder wrote
   * `mode` directly, which 403'd for every ordinary merchant — and because it set local
   * state first and swallowed the error, the UI showed a rung the backend did not have.
   * Stripping here means no caller can reintroduce that by spreading a GET response
   * (which does carry `mode`) straight back into a PUT.
   */
  saveAuthority: (
    workspaceId: string,
    data: AuthorityConfig,
    client = apiClient
  ): Promise<ApiResponse<AuthorityConfig>> => {
    const body = {
      rung: data.rung,
      exceptions: data.exceptions,
      ...(data.migratedAt !== undefined ? { migratedAt: data.migratedAt } : {}),
      ...(data.expectedUpdatedAt !== undefined ? { expectedUpdatedAt: data.expectedUpdatedAt } : {})
    }
    return client.put(`/workspaces/${workspaceId}/governance/authority`, body)
  },

  getEngagement: (workspaceId: string): Promise<ApiResponse<EngagementRules>> =>
    apiClient.get(`/workspaces/${workspaceId}/governance/engagement`),
  saveEngagement: (workspaceId: string, data: EngagementRules): Promise<ApiResponse<EngagementRules>> =>
    apiClient.put(`/workspaces/${workspaceId}/governance/engagement`, data),
  getNotificationPrefs: (workspaceId: string): Promise<ApiResponse<NotificationPrefs>> =>
    apiClient.get(`/workspaces/${workspaceId}/governance/notifications`),
  saveNotificationPrefs: (workspaceId: string, data: NotificationPrefs): Promise<ApiResponse<NotificationPrefs>> =>
    apiClient.put(`/workspaces/${workspaceId}/governance/notifications`, data),
  getEscalations: (workspaceId: string, status = 'pending'): Promise<ApiResponse<{ status: string; escalations: EscalationItem[] }>> =>
    apiClient.get(`/workspaces/${workspaceId}/escalations?status=${status}`),
  resolveEscalation: (workspaceId: string, notificationId: string, decision: 'approve' | 'reject', data: { createdAt: string; comment?: string }): Promise<ApiResponse<{ notificationId: string; status: string }>> =>
    apiClient.post(`/workspaces/${workspaceId}/escalations/${notificationId}/${decision}`, data),
  // Escalate-and-Return: customer questions + the merchant-answers knowledge store
  getQuestions: (workspaceId: string, status = 'pending'): Promise<ApiResponse<{ status: string; questions: QuestionItem[] }>> =>
    apiClient.get(`/workspaces/${workspaceId}/governance/questions?status=${status}`),
  answerQuestion: (workspaceId: string, notificationId: string, data: { createdAt: string; answerText: string }): Promise<ApiResponse<{ notificationId: string; status: string; returnStatus?: string }>> =>
    apiClient.post(`/workspaces/${workspaceId}/governance/questions/${notificationId}/answer`, data),
  declineQuestion: (workspaceId: string, notificationId: string, data: { createdAt: string }): Promise<ApiResponse<{ notificationId: string; status: string; returnStatus?: string }>> =>
    apiClient.post(`/workspaces/${workspaceId}/governance/questions/${notificationId}/decline`, data),
  getMerchantAnswers: (workspaceId: string): Promise<ApiResponse<{ answers: MerchantAnswerItem[]; exhaustive?: boolean; errorCode?: string | null }>> =>
    apiClient.get(`/workspaces/${workspaceId}/governance/answers`),
  updateMerchantAnswer: (workspaceId: string, fingerprint: string, data: { answerText?: string; expiresAt?: string | null }): Promise<ApiResponse<{ fingerprint: string; updated: boolean }>> =>
    apiClient.put(`/workspaces/${workspaceId}/governance/answers/${fingerprint}`, data),
  deleteMerchantAnswer: (workspaceId: string, fingerprint: string): Promise<ApiResponse<{ fingerprint: string; deleted: boolean }>> =>
    apiClient.delete(`/workspaces/${workspaceId}/governance/answers/${fingerprint}`),
  getBrandVoice: (workspaceId: string): Promise<ApiResponse<BrandVoice>> =>
    apiClient.get(`/workspaces/${workspaceId}/governance/brand-voice`),
  saveBrandVoice: (workspaceId: string, data: BrandVoice): Promise<ApiResponse<BrandVoice>> =>
    apiClient.put(`/workspaces/${workspaceId}/governance/brand-voice`, data),
  previewBrandVoice: (workspaceId: string, data: { scenario: string; brandVoice: BrandVoice }): Promise<ApiResponse<{ response: string }>> =>
    apiClient.post(`/workspaces/${workspaceId}/governance/brand-voice/preview`, data),

  // ── The derived voice (MAR-106) ───────────────────────────────────────────────────────
  //
  // Four routes, one screen. ⚠️ Every refusal here is keyed on `details.reason` by the caller,
  // never on `error`, which is prose and gets reworded.

  /** The review card's read. Answers one of four states and the RAW derived block. */
  getVoiceReview: (workspaceId: string, client = apiClient): Promise<ApiResponse<VoiceReview>> =>
    client.get(`/workspaces/${workspaceId}/governance/voice`),

  /**
   * Ask her to read the storefront. 202 — the deriver runs async and the screen polls.
   *
   * ⚠️ Its only callers are the merchant's own buttons — the review card and the Brand overview
   * (MAR-384), each a click on "Read my storefront" / "Read it again".
   * Nothing derives at connect and nothing derives lazily on first view: that would spend a
   * storefront fetch and a Bedrock call because a tab was opened (`app-ia-v5.md` §7.4).
   */
  deriveVoice: (workspaceId: string, client = apiClient): Promise<ApiResponse<VoiceReview>> =>
    client.post(`/workspaces/${workspaceId}/governance/voice/derive`),

  /**
   * The merchant's act, and the only writer of `status: 'confirmed'` anywhere.
   *
   * ⚠️ `derivedAt` identifies the proposal they were LOOKING AT. A re-derive landing between
   * render and click is answered 409 `derivation_superseded` rather than silently confirmed —
   * confirming a block nobody read is the rubber stamp this screen exists to prevent. So the
   * caller must pass the `derivedAt` it rendered, never the freshest one it knows about.
   */
  confirmVoice: (
    workspaceId: string,
    data: { derivedAt: string },
    client = apiClient
  ): Promise<ApiResponse<VoiceReview>> =>
    client.post(`/workspaces/${workspaceId}/governance/voice/confirm`, data),

  /**
   * A correction, which CONFIRMS in the same act — they could not be two acts, because a
   * correction written before a confirm was removed by that confirm and the derivation won at
   * read time (`app-ia-v5.md` §7.5).
   *
   * ⚠️ `traits` carries ONLY the traits whose value the merchant changed. Sending all four
   * stamps a merchant marker on traits they never touched and freezes those against every
   * future derivation — the gap §7.5 recorded as owed, and the reason this signature takes a
   * `Partial`.
   */
  correctVoice: (
    workspaceId: string,
    data: { derivedAt: string; traits: Partial<CorrectableTraits> },
    client = apiClient
  ): Promise<ApiResponse<VoiceReview>> =>
    client.post(`/workspaces/${workspaceId}/governance/voice/correct`, data),
  sendTestEmail: (workspaceId: string, data: TestEmailRequest): Promise<ApiResponse<TestEmailResponse>> =>
    apiClient.post(`/workspaces/${workspaceId}/email/test`, data),
  sendTestSms: (workspaceId: string, data: TestSmsRequest): Promise<ApiResponse<TestSmsResponse>> =>
    apiClient.post(`/workspaces/${workspaceId}/sms/test`, data),
}

// Activity Feed API
/**
 * The typed key a campaign's `skip`-mirrored row is counted under (MAR-64). Derived from the
 * receipt class by the activity feed — `wait` is `agent_judgment`, every other class is its
 * own key — so this union is derived from `DecisionClass` rather than restated: a new class
 * is a new key, and `SKIP_REASON_LABEL` (a `Record` over it) fails to compile until it has
 * words. Never a reason parsed out of the agent's prose.
 */
export type SkipReason = 'agent_judgment' | Exclude<DecisionClass, 'wait'>

/** One per-person row behind an aggregated campaign. `receipt` is absent from older feeds. */
export interface CampaignDecisionRow {
  id: string
  timestamp?: string
  status?: string
  outcome?: string
  customerId?: string
  reasoning?: string
  skipReason?: SkipReason
  receipt?: Receipt
}

export interface Activity {
  id: string
  type: 'agent_decision' | 'cart_recovery' | 'browse_followup' | 'comparison' | 'inspiration' | 'conversation' | 'back_in_stock' | 'price_drop' | 'shadow_decision' | 'discount' | 'product_feedback' | 'exit_intervention' | 'proactive_greeting' | 'contact_capture'
  title: string
  description: string
  timestamp: string
  /**
   * `reply` = an `ANS#` reply record (MAR-110): a reply Marva sent that is not a decision.
   * `merchant_action` = an applied merchant-action receipt (`act_…`, MAR-175): the MERCHANT's own
   * change, confirmed from an Ask proposal — not a judgment, so no why and no feedback.
   */
  source: 'touchpoint' | 'email' | 'conversation' | 'campaign' | 'decision' | 'shadow' | 'discount' | 'reply' | 'merchant_action'
  // A union, not a bare string, so an unhandled state is a compile error rather than silently
  // falling through actionState() to "Sent". That fall-through is why 'escalated' rows claimed
  // to have been sent for months. Mirrors DecisionSchema.status in @marva/shared/schemas, plus
  // 'aggregated', which the activity feed synthesises for rolled-up campaign rows.
  status?:
    | 'scheduled' | 'dispatched' | 'executed' | 'cancelled' | 'expired' | 'failed'
    | 'suppressed' | 'shadowed' | 'escalated' | 'pending_delivery' | 'delivered'
    | 'decided'
    | 'aggregated'

  // Decision-specific fields (Phase 19)
  trigger?: string
  channel?: string
  /** Legacy mirror — present only on a row written before the MAR-65 cutover. Never read it; read `receipt`. */
  action?: 'engage' | 'skip' | 'wait'
  reasoning?: string

  // What happened, typed (MAR-64). `receipt` is the shared classifier's answer, computed by
  // the activity feed; a surface reads its `class`, never `action`/`status` (the legacy
  // mirror — a failure row is `skip`, a cooldown-blocked help is `engage`). Absent from older
  // feeds, in which case `receiptOf()` runs the same classifier on the fields below.
  //
  // ⚠️ `reasoning` above is null on a current feed wherever `receipt.showsReasoning` is false
  // (a decision failure writes its error text there). An OLDER feed still sends it raw, so a
  // surface reads it through `visibleReasoning(a)`, never directly.
  receipt?: Receipt
  /**
   * The customer asked to be forgotten (MAR-252): the decision's text is gone — `message`,
   * `reasoning` and `content` carry none of it, never the erasure sentinel — and a surface says
   * so ("This customer asked to be forgotten — what was said is gone.") rather than showing an
   * empty body. Absent from an older feed, which may still send `'[REDACTED]'` raw.
   */
  erased?: boolean
  // The ledger line's own fields (MAR-110) — on every ledger item, and on a decision's DETAIL
  // response too, so a deep-linked drawer titles itself exactly as its row does.
  headline?: string
  // MAR-246: Marva's one-sentence why, or null. A ledger ROW never carries the raw reasoning
  // (`reasoning` is null on every line); the DETAIL response keeps it for the drawer.
  why?: string | null
  filter?: LedgerFilter | null
  chip?: string | null
  // Present only when a join could not be READ — distinct from "there was none".
  enrichmentUnavailable?: Array<'message' | 'events' | 'conversation' | 'outbound' | 'transcript' | 'name'>
  // Ledger only: the CONVERSATION was not looked up (the page's lookup cap was spent).
  enrichmentSkipped?: true
  // Ledger only: the customer's NAME was not looked up (the name cap was spent) — the line already
  // reads "a customer", so nothing else says so. Never the conversation's copy.
  nameSkipped?: true
  // Decision rows (MAR-246): why the gateway wrote no Ledger line (`ledger` is null) — the
  // judgment or channel changed after she wrote it, it failed validation, or it named the customer.
  ledgerDroppedReason?: 'judgment_changed' | 'channel_changed' | 'invalid' | 'names_customer' | null
  judgment?: { kind?: 'help' | 'wait' | 'escalate'; source?: string; reasoning?: string; decidedAt?: string } | null
  disposition?: Disposition | null
  // Typed pair only: the feed ships no failure message on this field, and gates `reasoning`
  // / `description` by `receipt.showsReasoning`.
  decisionFailure?: { stage: string | null; code: string | null } | null
  eligibility?: { kind?: string; reasonCode?: string } | null
  suppressionReason?: string | null
  cancellationReason?: string | null
  timing?: string // 'now', 'delay_1h', 'delay_24h', etc.
  message?: string // Generated message for governance review

  // Delivery result for socket channels (chat/popup/reaction/contact_capture):
  // whether the visitor actually saw the message. 'queued' replays on their
  // next visit and flips to 'delivered' when it renders. Absent for email/SMS
  // sends and rows that predate delivery stamping.
  deliveryStatus?: 'delivered' | 'queued' | 'failed'

  // WAS SOMEONE ACTUALLY REACHED, and how well do we know? The grade travels with
  // the answer so a socket ack is never read as a carrier receipt (mirrors
  // DecisionSchema.deliveryProof):
  //   'confirmed' — the channel told us it landed (SMS DLR, SES DELIVERY)
  //   'rendered'  — the client told us it was on screen
  //   'accepted'  — API Gateway took the frame; NOT proof anyone saw it
  //   'none'      — nothing came back
  //   'refuted'   — positive evidence it did NOT land (a hard bounce)
  // For an engage this is a more honest signal than `status`, which is stamped at
  // dispatch: a row reading 'executed' with 'refuted' bounced, and must never be
  // shown to a merchant as "Sent".
  deliveryProof?: 'confirmed' | 'rendered' | 'accepted' | 'none' | 'refuted'
  deliveryProofAt?: string

  // Outcome tracking
  outcome?: string // 'converted', 'email_opened', 'email_clicked', etc.
  outcomeAt?: string
  revenue?: number
  eventCount?: number

  // Key context fields for UI display
  cartValue?: number
  productTitle?: string
  itemCount?: number
  customerSegment?: string // 'high_value', 'at_risk', etc.
  lifecycleStage?: string // 'new', 'repeat_customer', etc.
  // Trigger origin — explains *where* the trigger fired so the row isn't
  // just labelled "Agent Decision" with no context.
  signalType?: string // e.g. 'high_intent_anonymous', 'momentum_stall'
  intentType?: string // e.g. 'exit_intent_cart', 'soft_exit'
  pageType?: string // e.g. 'product', 'collection', 'checkout'
  tier?: string // e.g. 'critical', 'high', 'medium' (exit intent tier)

  // Discount info
  discount?: {
    type?: string
    percent?: number
    code?: string
    expiry?: string
  }

  // Full snapshots for detail view
  customerSnapshot?: {
    valueSegment?: string
    lifecycleStage?: string
    communicationPreference?: string
  }
  triggerContext?: Record<string, unknown>

  metadata?: {
    customerId?: string
    visitorId?: string
    recipientEmail?: string
    cartId?: string
    opened?: boolean
    clicked?: boolean
    discountCode?: string
    // Discount-activity metadata (from the discount-codes table)
    code?: string
    discountPercent?: number
    channel?: string
    reason?: string
    orderId?: string
    expiresAt?: string
    // Conversation-specific metadata
    sessionId?: string
    messageCount?: number
    duration?: number
    topic?: string
    sentiment?: number
    sentimentLabel?: string
    outcome?: string
    outcomeLabel?: string
    // Shadow decision projected impact
    projectedImpact?: {
      expectedRecovery?: number
      conversionProbability?: number
      discountCost?: number
    }
    // Aggregated campaign metadata
    isAggregated?: boolean
    totalScheduled?: number
    // Confirmed sends only (a send site said it went). `totalSending` is the
    // handed-off-but-unconfirmed remainder; the rate denominators below are the
    // two added together, so a dispatched row is never dropped from the line.
    totalSent?: number
    totalSending?: number
    totalHeldBack?: number
    totalOpened?: number
    totalClicked?: number
    totalConverted?: number
    totalRevenue?: number
    // ⚠️ Deprecated legacy totals (counted from action/status): read byClass instead.
    totalSkipped?: number
    skipReasons?: Partial<Record<SkipReason, number>>
    // Every entry in the campaign by receipt class, zeros included.
    byClass?: Partial<Record<DecisionClass, number>>
    // Why policy withheld the helps it withheld, by typed reason code.
    blockedReasons?: Record<string, number>
    // Helps waiting on the merchant's approval — NOT in totalHeldBack.
    totalAwaitingApproval?: number
    openRate?: string
    clickRate?: string
    conversionRate?: string
    decisionIds?: string[]
    productInfo?: {
      productId?: string
      title?: string
    }
    emailIds?: string[]
  }
  content?: {
    subject?: string
    preheader?: string
    bodyText?: string
    bodyHtml?: string
    message?: string // Agent-generated content (email body, chat message, prompt text)
    comparisonDifferentiators?: Array<{ label?: string; winner?: string; explanation?: string }>
    discountDecisionReason?: string
    timingReasoning?: string
    // Conversation-specific content
    summary?: string
    // The actual exchange, from the conversations table (detail endpoint only)
    transcript?: Array<{
      role: 'customer' | 'assistant' | 'system'
      content: string
      timestamp?: string
      channel?: string
      compacted?: boolean
    }>
    productsDiscussed?: string[]
    questionsAsked?: string[]
    objectionsRaised?: string[]
    openItems?: string[]
    customerStyle?: string
    sentimentProgression?: number[]
    // Aggregated campaign content
    groupName?: string
    productInfo?: {
      productId?: string
      title?: string
    }
    sampleSubject?: string
    sampleMessage?: string
  }
  // Decision events, detail endpoint only (send/open/click timeline)
  events?: Array<{ eventType: string; timestamp?: string; metadata?: Record<string, unknown> }>
  // Aggregated campaign detail only: the per-person decisions behind the rollup
  decisions?: {
    byClass?: Partial<Record<DecisionClass, number>>
    engaged: CampaignDecisionRow[]
    skipped: CampaignDecisionRow[]
  }
  feedback?: {
    feedbackType: 'up' | 'down'
    comment?: string
    createdAt: string
  } | null
}

/**
 * The feedback body (activity-feed `submitFeedback`). The PUT is keyed on the activity, so a second
 * answer REPLACES the first; there is no delete. `suggestedAlternative` keeps only these four keys.
 */
/** The feedback body; `feedbackCategory` is one of `lib/feedbackCategories`. */
export interface ActivityFeedback {
  feedbackType: 'up' | 'down'
  comment?: string
  feedbackCategory?: FeedbackCategory
  feedbackDetail?: string
  suggestedAlternative?: {
    action?: string | null
    channel?: string | null
    timing?: string | null
    toneAdjustment?: string | null
  }
}

export interface ActivityStats {
  period: string
  totalActivities: number
  byType: Record<string, number>
  byStatus: Record<string, number>
  feedbackCount: number
}

/**
 * The Ledger view of the activity route (MAR-110): one line per decision, shadow included.
 * `filter` and `channel` are multi-select (OR within, AND across); `from`/`to` are inclusive
 * ISO bounds. Page with `cursor` until `hasMore` is false. `partial` means the server's read
 * budget ran out before the page filled — show what came back and offer the next page; it is
 * not the end. See docs/reference/api/governance.md.
 */
export interface LedgerParams {
  filter?: readonly LedgerFilter[]
  channel?: readonly LedgerChannel[]
  /** A stored positive outcome (e.g. `conversion`) — the API accepts it; no screen uses it yet. */
  outcome?: string
  from?: string
  to?: string
  cursor?: string | null
  limit?: number
}

/** An `ANS#` reply record's pointers (MAR-110) — ids only, never text. */
export interface LedgerReplyPointers {
  conversationId: string | null
  sessionId: string | null
  outbound: string | null
  template: string | null
  suitability: string | null
}

/** The shared vocabulary's send states — read from it, never restated. */
export type LedgerReplySendState = ReplySendState

export interface LedgerItem extends Activity {
  /**
   * The line's title, the customer's name already filled in (MAR-246): Marva's own title, the
   * deterministic fallback, a reply's `summarizeReply`, or a campaign's "New-season picks — 14
   * customers". Never states the outcome — `chip` does.
   */
  headline: string
  /** Marva's one-sentence why, or null. Never the raw reasoning. */
  why?: string | null
  /** Campaign lines only: every channel its members used (`channel` is set when there is one). */
  channels?: string[]
  /**
   * A short-cluster rollup (MAR-387): several anonymous chats on the generic title, or greetings
   * she chose not to give, folded into one campaign-shaped line (`source: 'campaign'`, a `grp_` id).
   */
  rollup?: LedgerRollupKind
  /** Which generic chat title the rollup folded (`question` | `reply`); null for greetings. */
  rollupVariant?: 'question' | 'reply' | null
  /** `composed`: the Ledger composed this title from the lines it folds — she did not write it. */
  titleSource?: 'composed'
  /** Rollups only: the oldest folded line's time (`timestamp` is the newest's). */
  timestampFrom?: string
  /** Merchant-action lines only (`act_…`, MAR-175): which kind of change the merchant applied. */
  capability?: ProposalCapability
  /** Reply lines only: the path that answered (`sms_reply`, `chat_template`, …). */
  replySource?: string
  /** Reply lines only: what actually happened to the send, as the producer stated it. */
  sendState?: LedgerReplySendState
  /** Reply lines only: how many times the send went out (a retry that sent twice says 2). */
  sendCount?: number
  /** Reply lines only: pointers to the conversation / outbound message / template / pack. */
  pointers?: LedgerReplyPointers
  /** Rows this line stands for (one line per conversation); 1 for a standalone row. */
  turns?: number
  /**
   * Campaign lines only: distinct people the line reached — the count its headline states. One
   * shopper decided on twice is one person here and two in `turns`.
   */
  people?: number
  /** The ids of those rows, newest first (the anchor's id first), capped at 50. */
  memberIds?: string[]
  /** The merchant's filter word this line belongs to; null = shown only under "all". */
  filter: LedgerFilter | null
  /** Row chip copy for the class, or null. */
  chip: string | null
  /** A row the server could not read: only id/timestamp/headline are meaningful. */
  unreadable?: true
  /**
   * A chat reply (the fast path's `customer_message` row): its stock reasoning is never sent;
   * the why is its conversation's summary, or absent.
   */
  reasoningSource?: 'conversation'
  conversation?: {
    conversationId: string | null
    summary: string | null
    topic: string | null
    messageCount: number | null
    outcome: string | null
  } | null
}

/**
 * The detail route's answer for a reply id (`rep_…`, MAR-110): the ledger line's own fields plus
 * three reads, each of which degrades into `enrichmentUnavailable` rather than failing the drawer.
 * `outbound` is the outbound message's status/sentAt/errorCode only — never its body or recipient.
 */
export interface LedgerReplyDetail extends LedgerItem {
  source: 'reply'
  outbound?: { status: string | null; sentAt: string | null; errorCode: string | null } | null
  transcript?: NonNullable<Activity['content']>['transcript'] | null
}

/**
 * The detail route's answer for an applied merchant-action receipt (`act_…`, MAR-175). The
 * merchant's own change: `before`/`after` are the exact states the receipt recorded.
 *
 * ⚠️ A `customer_answer` receipt holds NO question — `before` is null and nothing here may be
 * read as the customer's words. After a per-customer erasure `after.answerText` is the literal
 * `'[REDACTED]'` and `redacted` is true.
 */
export interface LedgerMerchantActionDetail {
  id: string
  source: 'merchant_action'
  capability: ProposalCapability
  headline: string
  summary: string
  timestamp: string
  appliedAt: string
  before: ProposalAuthorityState | null
  after: ProposalAuthorityState | ProposalAnswerAfter
  actorId: string
  status: 'applied'
  result: { mode?: 'shadow' | 'live'; returnStatus?: string } | null
  redacted: boolean
}

/** The short-cluster rollups (MAR-387) — `@marva/shared/ledger-vocabulary` `LEDGER_ROLLUP_KINDS`. */
export type LedgerRollupKind = 'anonymous_chat' | 'greeting_wait'

/** One member of a campaign line's detail (MAR-246). */
export interface LedgerCampaignMember {
  id: string
  timestamp: string
  /** Rollup members only: the rows this member's own line stands for ("· 3 replies"). */
  turns?: number
  /** "James O.", or null — the headline then reads "a customer". */
  name: string | null
  headline: string
  why: string | null
  receipt: Receipt
  filter: LedgerFilter | null
  chip: string | null
  channel: string | null
  status: string | null
  outcome: string | null
  revenue: number | null
  /** The name cap was spent: `name` is null and the headline reads "a customer". */
  nameSkipped?: true
  enrichmentSkipped?: true
  enrichmentUnavailable?: Array<'name'>
}

/**
 * The detail route's answer for a campaign line id (`grp_…`, MAR-246): one local day's members of
 * that line, the Journal rollup's counts in `metadata` (`byClass`, `totalSent`, …), and
 * `partial` when the day held more than the server reads (500 rows).
 */
export interface LedgerCampaignDetail {
  id: string
  source: 'campaign'
  type: string
  trigger: string
  /** The one filter word every member shares, or null when the line mixes outcomes. */
  filter: LedgerFilter | null
  /** The filter set the line was cut under (null = all) — the members are exactly those it counted. */
  filters: LedgerFilter[] | null
  /** Null when a busy day's read budget ran out before reaching any of this campaign's members. */
  timestamp: string | null
  /** A rollup (MAR-387) is minutes, not a local day: both are null for one. */
  localDay: string | null
  timezone: string | null
  /** Rollups only (MAR-387): which kind, and that the title is composed rather than hers. */
  rollup?: LedgerRollupKind
  rollupVariant?: 'question' | 'reply' | null
  titleSource?: 'composed'
  timestampFrom?: string | null
  /**
   * Rollups only: how many lines the list folded. `decisions` holds exactly those unless
   * `partial` (the re-read was cut short, or the rows changed since the list read them).
   */
  listed?: number
  headline: string
  why: null
  chip: string | null
  /** Distinct people the campaign reached — the same count as the headline (MAR-246). */
  people: number
  /** Decisions the campaign made; one shopper decided on twice counts two here. */
  turns: number
  partial: boolean
  metadata: {
    isAggregated: true
    byClass: Partial<Record<DecisionClass, number>>
    totalSent: number
    totalSending: number
    totalScheduled: number
    totalHeldBack: number
    totalAwaitingApproval: number
    totalOpened: number
    totalClicked: number
    totalConverted: number
    totalRevenue: number
    decisionIds: string[]
    [key: string]: unknown
  }
  decisions: LedgerCampaignMember[]
}

export interface LedgerPage {
  view: 'ledger'
  items: LedgerItem[]
  count: number
  cursor: string | null
  hasMore: boolean
  partial: boolean
  /** How many rows on this page the server could not read (shown as minimal lines). */
  unreadable: number
}

/** The "Worth a look" tiers (MAR-388) — `@marva/shared/ledger-vocabulary` `LEDGER_WORTH_TIERS`. */
export type LedgerWorthTier = 'failed' | 'asked' | 'blocked' | 'chose_not' | 'claimed'

/** A "Worth a look" pick: an ordinary ledger line plus why it was picked. */
export interface LedgerWorthItem extends LedgerItem {
  worth: { tier: LedgerWorthTier; reason: string }
}

/**
 * `GET /activity?view=worth_a_look` (MAR-388): up to three decisions across the window, tier by
 * tier, newest first within a tier. `partial` when the window held more than the server reads
 * (its oldest part was not considered); `readThrough` is then the oldest instant it read.
 */
export interface LedgerWorthPage {
  view: 'worth_a_look'
  items: LedgerWorthItem[]
  count: number
  partial: boolean
  readThrough: string | null
  /** Rows that could not be classified or formatted (logged server-side, never a pick). */
  unreadable: number
  pagesRead: number
}

export function ledgerQuery(params: LedgerParams = {}): string {
  const q = new URLSearchParams({ view: 'ledger' })
  if (params.filter && params.filter.length > 0) q.set('filter', params.filter.join(','))
  if (params.channel && params.channel.length > 0) q.set('channel', params.channel.join(','))
  if (params.outcome) q.set('outcome', params.outcome)
  if (params.from) q.set('from', params.from)
  if (params.to) q.set('to', params.to)
  if (params.cursor) q.set('cursor', params.cursor)
  if (params.limit) q.set('limit', String(params.limit))
  return q.toString()
}

export const activityApi = {
  ledger: (workspaceId: string, params?: LedgerParams): Promise<ApiResponse<LedgerPage>> =>
    apiClient.get(`/workspaces/${workspaceId}/activity?${ledgerQuery(params)}`),
  worthALook: (workspaceId: string, params: { from?: string; to?: string } = {}): Promise<ApiResponse<LedgerWorthPage>> => {
    const q = new URLSearchParams({ view: 'worth_a_look' })
    if (params.from) q.set('from', params.from)
    if (params.to) q.set('to', params.to)
    return apiClient.get(`/workspaces/${workspaceId}/activity?${q.toString()}`)
  },
  list: (workspaceId: string, params?: { type?: string; limit?: number }): Promise<ApiResponse<{ activities: Activity[]; count: number; hasMore: boolean }>> => {
    const searchParams = new URLSearchParams()
    if (params?.type) searchParams.set('type', params.type)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    const query = searchParams.toString()
    return apiClient.get(`/workspaces/${workspaceId}/activity${query ? `?${query}` : ''}`)
  },
  getDetail: (workspaceId: string, activityId: string): Promise<ApiResponse<Activity>> =>
    // The id can come from the URL (`/ledger?decision=`): encode it, so it can never name another path.
    apiClient.get(`/workspaces/${workspaceId}/activity/${encodeURIComponent(activityId)}`),
  submitFeedback: (workspaceId: string, activityId: string, data: ActivityFeedback): Promise<ApiResponse<{ feedbackId: string; message: string }>> =>
    // keepalive: the Ledger sends a held answer as the page goes (`pagehide`); a small body, so
    // the browser may finish the request after the document is gone.
    apiClient.post(`/workspaces/${workspaceId}/activity/${encodeURIComponent(activityId)}/feedback`, data, undefined, {
      keepalive: true,
    }),
  getStats: (workspaceId: string, days?: number): Promise<ApiResponse<ActivityStats>> =>
    apiClient.get(`/workspaces/${workspaceId}/activity/stats${days ? `?days=${days}` : ''}`),
}

// Dashboard API Types
export interface DashboardStats {
  mode: 'shadow' | 'live'
  trial?: {
    daysRemaining: number | null
    endsAt: string | null
    startedAt: string | null
  }
  billing?: {
    currentPeriod: {
      start: string
      end: string
    }
    daysRemaining: number
  }
  // Shadow mode metrics (at top level from getShadowStats)
  projectedRecovery?: number
  decisionsToday?: number
  conversionsProjected?: number
  approvalRate?: number
  total?: number
  approved?: number
  rejected?: number
  pending?: number
  // Trigger-type breakdown for shadow mode
  byTrigger?: Record<string, TriggerBreakdown>
  // Legacy metrics object for compatibility
  metrics?: {
    // Live mode metrics
    revenueAttributed?: number
    actionsToday?: number
    conversions?: number
    roi?: number
  }
  // Legacy feedback object for compatibility
  feedback?: {
    approved: number
    rejected: number
    pending: number
    total: number
  }
}

export interface DashboardUsage {
  period: string  // e.g., "2025-01"
  tier: 'starter' | 'scale' | 'enterprise'
  metrics: {
    mau?: { used: number; limit: number; percentage?: number; warning?: string | null }
    conversations?: { used: number; limit: number; percentage?: number; warning?: string | null }
    emails?: { used: number; limit: number; percentage?: number; warning?: string | null }
  }
  gracePeriod: unknown | null
  pausedMetrics: string[]
  updatedAt: string | null
}

export interface ShadowDecision {
  decisionId: string  // Actual field name from database
  type?: string
  trigger?: string  // Phase 21: decisions use 'trigger' field
  createdAt: string
  // Decision details
  /** Legacy mirror — present only on a row written before the MAR-65 cutover. Never read it; read `receipt`. */
  action?: 'engage' | 'skip' | 'defer'
  channel?: 'email' | 'sms' | 'chat' | 'reaction_prompt'
  // ⚠️ Never render directly — read `shadowReasoning(d)` (lib/activityNarrative). A current
  // Lambda nulls a failure's error text here; an older one sends it raw (MAR-64).
  reasoning?: string | null
  /** The shared classifier's receipt, classified as `shadowed`. Absent from an older Lambda. */
  receipt?: Receipt
  message?: string
  // Context
  triggerContext?: Record<string, unknown>
  customerSnapshot?: Record<string, unknown>
  customer?: {
    email?: string
    customerId?: string
    visitorId?: string
  }
  // Impact
  projectedImpact?: {
    expectedRecovery?: number
    cartValue?: number
    conversionProbability?: number
  }
  feedback?: {
    status: 'pending' | 'approved' | 'rejected'
    reviewedAt?: string
    notes?: string
  }
  // Structured email the agent drafted (shown on the held-for-approval card so
  // the merchant can see what would actually be sent).
  emailContent?: {
    subject?: string
    body?: string
    bodyText?: string
  }
}

/**
 * `GET /workspaces/{id}/shadow/decisions` — `shadowInterceptor.getShadowDecisions`'
 * result passed through as `data`. There is no `pagination` object.
 *
 * ⚠️ `nextKey` means "the server stopped with pages unscanned", not "more
 * matches exist": matches are accumulated across pages and then sliced to
 * `limit`, so a final page can cut matches and return no `nextKey`. Ask for
 * one more than you show to detect that (see `useNeedsYou`).
 */
export interface ShadowDecisionsResponse {
  decisions: ShadowDecision[]
  /** Base64 resume cursor, or `null` once the partition is fully scanned. */
  nextKey?: string | null
}

export interface DashboardActivity {
  id: string
  type: string
  createdAt: string
  summary: string
  metadata?: Record<string, unknown>
}

export interface DashboardActivityResponse {
  activities: DashboardActivity[]
  hasMore: boolean
}

export interface DashboardAttributionResponse {
  period: string
  summary: {
    totalRevenue: number
    totalAttributions: number
  }
  channels: {
    chat: { revenue: number; count: number }
    email: { revenue: number; count: number }
    engagement: { revenue: number; count: number }
  }
}

// Dashboard API
export const dashboardApi = {
  getStats: (workspaceId: string, period?: string): Promise<ApiResponse<DashboardStats>> => {
    const params = period ? `?period=${period}` : ''
    return apiClient.get(`/dashboard/stats?workspaceId=${workspaceId}${params ? `&${params.slice(1)}` : ''}`)
  },

  getUsage: (workspaceId: string): Promise<ApiResponse<DashboardUsage>> =>
    apiClient.get(`/dashboard/usage?workspaceId=${workspaceId}`),

  getShadowDecisions: (
    workspaceId: string,
    params?: { type?: string; status?: string; limit?: number; startKey?: string }
  ): Promise<ApiResponse<ShadowDecisionsResponse>> => {
    const searchParams = new URLSearchParams({ workspaceId })
    if (params?.type) searchParams.set('type', params.type)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.startKey) searchParams.set('startKey', params.startKey)
    return apiClient.get(`/dashboard/shadow?${searchParams.toString()}`)
  },

  getShadowDecision: (workspaceId: string, decisionId: string): Promise<ApiResponse<ShadowDecision>> =>
    apiClient.get(`/dashboard/shadow/${decisionId}?workspaceId=${workspaceId}`),

  submitFeedback: (
    workspaceId: string,
    decisionId: string,
    data: { status: 'approved' | 'rejected'; notes?: string }
  ): Promise<ApiResponse<{ success: boolean; decisionId: string; feedback: { status: string; reviewedAt: string } }>> =>
    apiClient.post(`/dashboard/shadow/${decisionId}/feedback?workspaceId=${workspaceId}`, data),

  getActivity: (workspaceId: string, limit?: number): Promise<ApiResponse<DashboardActivityResponse>> =>
    apiClient.get(`/dashboard/activity?workspaceId=${workspaceId}${limit ? `&limit=${limit}` : ''}`),

  getAttribution: (workspaceId: string, period?: string): Promise<ApiResponse<DashboardAttributionResponse>> =>
    apiClient.get(`/dashboard/attribution?workspaceId=${workspaceId}${period ? `&period=${period}` : ''}`),
}

// Subscription Billing API Types (Phase 11)
export interface TierLimits {
  mau: number
  conversations: number
  emails: number
}

export interface UsageMetric {
  used: number
  limit: number
  percentage: number
  warning: string | null
  // Present on metered metrics (conversations, emails) — PRD §3.4
  overage?: {
    allowance: number
    used: number
    chargeCents: number
  }
}

export interface SubscriptionStatus {
  status: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'paused' | 'expired'
  tier: 'starter' | 'scale' | 'enterprise'
  source: 'stripe' | 'invoice' | 'trial'
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  customLimits: TierLimits | null
  period: {
    type: 'trial' | 'billing'
    endsAt?: string
    start?: string
    end?: string
    daysRemaining: number
  }
  usage: {
    mau: UsageMetric
    conversations: UsageMetric
    emails: UsageMetric
  }
  /** How many workspaces `usage` sums (MAR-413). Absent from a server that predates it. */
  workspaceCount?: number
  /** How many of those the nightly usage enforcement acts on (not synthetic/shadow/disabled). */
  enforcedWorkspaceCount?: number
  billEstimate: {
    baseCents: number
    overageCents: number
    totalCents: number
  } | null
  availableTiers: Array<{
    id: string
    name: string
    monthlyPrice: string
    annualPrice: string
    annualMonthlyPrice: string
    limits: TierLimits
    popular?: boolean
    contactSales?: boolean
  }>
  annualDiscountPercent: number
}

export interface CheckoutSession {
  // Redirect flow (new subscription via Stripe Checkout)
  checkoutUrl?: string
  sessionId?: string
  // In-place flow (existing Stripe subscription switched with proration — no redirect)
  updated?: boolean
  tier?: string
  billingInterval?: 'month' | 'year'
}

export interface PortalSession {
  portalUrl: string
}

export interface EnterpriseActivationRequest {
  customLimits: {
    conversations: number
    messages: number
    profiles: number
    products: number
  }
  reason?: string
}

export interface EnterpriseActivationResponse {
  status: 'active'
  tier: 'enterprise'
  source: 'invoice'
  customLimits: {
    conversations: number
    messages: number
    profiles: number
    products: number
  }
  message: string
}

// Subscription Billing API
export const subscriptionApi = {
  getStatus: (accountId: string): Promise<ApiResponse<SubscriptionStatus>> =>
    apiClient.get('/subscriptions', { 'x-account-id': accountId }),

  createCheckout: (accountId: string, data: {
    tier: 'starter' | 'scale'
    billingInterval?: 'month' | 'year'
    successUrl: string
    cancelUrl: string
  }): Promise<ApiResponse<CheckoutSession>> =>
    apiClient.post('/subscriptions/checkout', data, { 'x-account-id': accountId }),

  createPortal: (accountId: string, data: {
    returnUrl: string
  }): Promise<ApiResponse<PortalSession>> =>
    apiClient.post('/subscriptions/portal', data, { 'x-account-id': accountId }),

  activateEnterprise: (accountId: string, data: EnterpriseActivationRequest): Promise<ApiResponse<EnterpriseActivationResponse>> =>
    apiClient.post('/subscriptions/enterprise', data, { 'x-account-id': accountId }),
}

// Sales Management API Types
//
// O1 item 3 phase 5 inverted this surface. `POST /sales` no longer *creates* a sale from
// facts the merchant re-types; it annotates a discount Shopify already told us about.
// The mechanical facts — value, scope, entitlements, prerequisites, dates — are Shopify's
// and are rejected with a 400 if sent, because a second copy of them drifts the moment
// the merchant edits the discount in Shopify.

/**
 * Detected path (§3.8). `linkedPriceRuleId` is the row's identity and is required.
 *
 * ⚠️ It carries the discount's OWN id — `ActiveDiscount.discountId` — because that is
 * what `applyMentionRights` joins on and what `getActiveDiscount` keys by
 * (`sk = DISCOUNT#{discountId}`). `priceRuleId` is a legacy alias of the same value on
 * every row the current writer produces, but it is not the contract.
 *
 * `name` / `discountDisplay` are the merchant's own words for the discount and are
 * optional: absent them, Marva describes it truthfully from Shopify's own terms.
 */
export interface AnnotateDiscountRequest {
  linkedPriceRuleId: string
  name?: string
  discountDisplay?: string
  /**
   * ⚠️ Not a merchant-typed field on this path, and the panel does NOT send it. The
   * handler refuses a linked code discount only when NOBODY holds a code
   * (`!discountCode && !linked.code && linked.requiresCode !== false` → 400), and the
   * hydrated discount row carries the real one — so annotating a detected discount is
   * `{ linkedPriceRuleId }` and nothing else. Echoing the code back would store a
   * merchant-side copy of a Shopify fact that drifts the moment the merchant rotates
   * it; `applyMentionRights` reads `d.code` off the live discount row first anyway.
   * Left on the type for the rare row we hydrated without a code.
   */
  discountCode?: string
}

/**
 * The only shape `POST /sales` accepts. §3.8 originally kept a manual escape hatch for a
 * sale run through Shopify Functions or a third-party app; it was removed 2026-09-02
 * because a row with no `linkedPriceRuleId` never pairs with a discount, so the agent
 * never sees it — while it counts into `withheld` and stops the block giving the honest
 * negative. Such a store gets per-item was/now grounding instead.
 */
export type CreateSaleRequest = AnnotateDiscountRequest

/**
 * Only the merchant's own words are editable — everything else is Shopify's fact.
 * `null` clears a field back to "no words of mine"; `undefined` leaves it untouched
 * (the handler only writes a field when it is `!== undefined`, and JSON drops undefined).
 */
export interface UpdateSaleRequest {
  name?: string | null
  discountDisplay?: string | null
}

export interface Sale {
  saleId: string
  name: string
  discountDisplay: string
  discountCode?: string
  /**
   * `shopify_detected` is the only value anything writes now. `merchant_initiated` is
   * the pre-phase-5 value: those rows were hand-typed, carry no usable link, and are
   * never rendered to the agent — the panel shows them as legacy so they can be cleared.
   */
  source: 'shopify_detected' | 'merchant_initiated'
  /** Present only on a detected row; the join back to `ActiveDiscount.discountId`. */
  linkedPriceRuleId?: string
  status: 'active' | 'ended' | 'scheduled'
  /**
   * Absent means NO BOUNDARY — the merchant set no start — not "unknown", and it is now
   * the COMMON case: the writer only stores this when she supplies one (MAR-31). Declaring
   * it required was a lie the compiler believed, and the next `new Date(sale.startsAt)` in
   * the panel would have thrown on every ordinary grant. `createdAt` is when she granted it.
   */
  startsAt?: string
  endsAt?: string
  createdAt: string
}

export interface CreateSaleResponse {
  saleId: string
  status: string
  /** Always `shopify_detected` — the API decides this, not the caller. */
  source?: 'shopify_detected'
  linkedPriceRuleId?: string | null
  message: string
}

export interface ActiveDiscount {
  priceRuleId: string
  discountId?: string
  title: string
  // The real redemption code, hydrated from the Shopify Admin API. Only present on
  // code discounts — an automatic discount has nothing to type at checkout.
  code?: string
  requiresCode?: boolean
  discountType?: 'automatic' | 'code' | 'app'
  // O1 item 3 phase 1: discounts are hydrated from the Admin API, so a discount whose
  // terms we could not read now says so rather than reporting a confident 0%.
  valueType: 'percentage' | 'fixed_amount' | 'free_shipping' | 'bxgy' | 'unknown'
  value?: number
  // Shopify's own phrasing of the discount ("15% off entire order"), which beats
  // anything we can compose from the parts.
  summary?: string
  shortSummary?: string
  describable?: boolean
  /**
   * The API's own verdict: may Marva say anything about this discount at all — is it
   * live AND stateable? Computed server-side from the same `isStateable`
   * (`@marva/shared/active-sales`) that gates the agent and that `POST /sales` refuses
   * a grant against.
   *
   * ⚠️ The panel MUST NOT re-derive this. It used to, from `describable !== false`
   * alone, while the authoritative predicate additionally demands a known scope, a
   * renderable value/currency and a renderable minimum — so the panel offered a toggle
   * the grant endpoint then rejected, and badged rows "Always speakable" that the agent
   * silently withheld. Two predicates drift; one does not.
   *
   * Absent on a response from a backend older than this field. `speakability()` in
   * `components/setup/discountWording.ts` says what the panel does then — it falls back
   * to what it can see rather than calling every row unspeakable.
   */
  speakable?: boolean
  /**
   * Short machine-readable reason the row is not speakable (e.g. `terms_unreadable`,
   * `app_discount_opaque`). Null or absent when it is. The prose the merchant reads is
   * composed in the panel — this is the fact, not the sentence.
   */
  speakableReason?: string | null
  targetSelection?: 'all' | 'entitled'
  collectionIds?: string[]
  productIds?: string[]
  startsAt?: string
  expiresAt?: string
  source: 'shopify'
}

// Sales Management API
export const salesApi = {
  // Grant Marva mention rights over a discount Shopify detected. The only way to create a sale.
  create: (workspaceId: string, data: CreateSaleRequest): Promise<ApiResponse<CreateSaleResponse>> =>
    apiClient.post(`/workspaces/${workspaceId}/sales`, data),

  // List the sale rows for a workspace — i.e. which discounts Marva may raise.
  //
  // `truncated` carries the same meaning as on `listDiscounts`, and matters more here:
  // a mention right that fell off the end of a capped read renders as a toggle in the
  // OFF position, which states that the merchant granted nothing. Say the list is short
  // rather than letting its absences read as decisions.
  list: (workspaceId: string): Promise<ApiResponse<{ sales: Sale[]; truncated?: boolean }>> =>
    apiClient.get(`/workspaces/${workspaceId}/sales`),

  // Change the merchant's own words for a sale. Nothing mechanical is editable.
  update: (workspaceId: string, saleId: string, data: UpdateSaleRequest): Promise<ApiResponse<{ saleId: string; message: string }>> =>
    apiClient.put(`/workspaces/${workspaceId}/sales/${saleId}`, data),

  // Revoke mention rights. The discount keeps running in Shopify; Marva just stops
  // raising it unprompted.
  delete: (workspaceId: string, saleId: string): Promise<ApiResponse<{ saleId: string; message: string }>> =>
    apiClient.delete(`/workspaces/${workspaceId}/sales/${saleId}`),

  // End a sale early. Kept because the route is, but the panel revokes with delete():
  // a detected discount's dates are Shopify's to end, not ours.
  end: (workspaceId: string, saleId: string): Promise<ApiResponse<{ saleId: string; status: string; message: string }>> =>
    apiClient.post(`/workspaces/${workspaceId}/sales/${saleId}/end`, {}),

  // Everything Shopify has told us is running. This list IS the surface.
  //
  // `truncated` is true when the read stopped at its row cap. The list is then SHORT,
  // not complete, and the panel has to say so: "we could not show you everything" is a
  // different sentence from "there is nothing", and dropping the flag turns the first
  // into the second silently.
  listDiscounts: (workspaceId: string): Promise<ApiResponse<{ discounts: ActiveDiscount[]; truncated?: boolean }>> =>
    apiClient.get(`/workspaces/${workspaceId}/discounts`),
}

// ── Knowledge (App IA v5 phase 5b, MAR-204) ─────────────────────────────────────────────
//
// `services/customer-intelligence/knowledge-api` — what Marva believes about a person and
// why, and the merchant's verdict on a belief.
//
// ⚠️ Every refusal on these routes is keyed on `details.reason`, never on `error`. The
// tokens are `KNOWLEDGE_REASONS` in `@marva/shared/knowledge-vocabulary`; `error` is prose
// written for a human and gets reworded. `knowledgeRefusal` below is the only mapping of
// them in the web app.

/**
 * One source record behind a belief's evidence (MAR-390). `evidenceIndex` names the
 * `evidence[]` item it supports. `receipt` is present ONLY when the server proved the record
 * opens somewhere in this app — today an order the workspace's attribution credits to a
 * decision, opened as its Ledger receipt. No `receipt` → the item stays plain text.
 */
export interface EvidenceRef {
  kind: 'order' | 'conversation' | 'decision' | 'event'
  id: string
  evidenceIndex: number
  at: string | null
  receipt?: { decisionId: string }
}

/**
 * A believed fact, exactly as `toBelief` in `knowledge-api/src/reads.js` projects it — the
 * fields both subjects share. The id field is NOT shared: see `CustomerBelief` and
 * `ProductBelief`.
 */
export interface BeliefBase {
  type: string | null
  /** The sentence the merchant reads. */
  hypothesis: string | null
  confidence: number | null
  status: 'active' | 'confirmed' | 'refuted' | 'expired' | null
  /**
   * Auditable pointers to the records this theory came from — always rendered, never
   * summarised away. A believed fact with no provenance is a claim the merchant has no way
   * to judge, which is the whole reason this surface exists.
   */
  evidence: string[]
  /** MAR-390 — optional, so a response from a server that predates it still types. */
  evidenceRefs?: EvidenceRef[]
  evidenceFingerprint: string | null
  generatedAt: string | null
  /**
   * ⚠️ Both the optimistic-concurrency token AND the selector for the row the merchant was
   * shown. It goes back to the verdict route as `expectedUpdatedAt` **exactly as it
   * arrived** — never re-derived, and never read off a belief the client re-picked.
   */
  updatedAt: string | null
  settledAt: string | null
  settledBy: string | null
  settledSource: string | null
}

/**
 * A belief about a PERSON (`HYP#` row). ⚠️ `toBelief` names the id field as the subject
 * stores it — `hypothesisId` here, `beliefId` on a product — so the verdict a client sends
 * back names the same thing the shared settle selects on. Never read one off the other.
 */
export interface CustomerBelief extends BeliefBase {
  hypothesisId: string
}

/** A belief about a PRODUCT (`PBEL#` row, shadow-only). Its id is `beliefId`, never `hypothesisId`. */
export interface ProductBelief extends BeliefBase {
  beliefId: string
}

/** The customer belief, under the name existing code already uses. */
export type Belief = CustomerBelief

/** What she has read about this person. Facts about the record, with no write path. */
export interface KnowledgePerson {
  customerId: string | null
  /** The row's actual pk, opaque here. PR 2's erasure names the row with it. */
  customerRef: string | null
  shopifyCustomerId: string | null
  /** Stated by the server, never inferred from the shape of an id. */
  hasShopifyCustomerId: boolean
  firstName: string | null
  lastName: string | null
  /** `a••@example.com` — enough to tell two people apart, never the address itself. */
  emailMasked: string | null
  ordersCount: number | null
  totalSpent: number | string | null
  lifecycleStage: string | null
  valueSegment: string | null
  segments: string[]
  synthesizedDescription: string | null
  openItems: string[]
}

/**
 * Every status broken down, whether or not it is being shown.
 *
 * `active` + `confirmed` are the default view; `refuted` + `expired` need
 * `?includeSettled=true`. The counts are what the "and N settled" affordance is built from,
 * so it can be offered without a speculative second read.
 */
export interface BeliefCounts {
  active: number
  confirmed: number
  refuted: number
  expired: number
}

export interface CustomerKnowledge {
  person: KnowledgePerson
  beliefs: Belief[]
  counts: BeliefCounts
}

/**
 * The verdict route's success body.
 *
 * ⚠️ `alreadySettled: true` is a **200 and a SUCCESS** — a replay of a verdict the row is
 * already in, carrying `reason: 'hypothesis_already_settled'`. Rendering it as a failure
 * teaches a merchant to distrust a button that worked.
 */
export interface SettleBeliefResult<B extends BeliefBase = CustomerBelief> {
  belief: B | null
  alreadySettled: boolean
  reason?: string
}

// ── Erasure (MAR-204 PR 2) ──────────────────────────────────────────────────────────────
//
// `docs/brand/product-tests.md` lists **single-action per-customer erasure** in the FLOOR —
// a gate, not a trade-off. Phase 5a built the door; until these calls existed nothing in
// the web app reached it, so the claim was true only for an operator with AWS access.

/**
 * Rows the engine set aside, by why it could not act on them.
 *
 * ⚠️ `otherAnchor` is **benign** and is deliberately never shown as a gap: those rows were
 * PROVEN to belong to somebody else, so withholding them misses nothing. The schema names
 * it `ERASURE_WITHHELD_BENIGN_COUNTERS`, and `erasureReceiptWithheldSomething` excludes it
 * for the same reason.
 */
export interface ErasureWithheldCounts {
  otherAnchor?: number
  ambiguous?: number
  unanchored?: number
}

/** What is demonstrably still there. Counts and booleans only — never a second copy of the subject. */
export interface ErasureResidual {
  withheldConversations?: ErasureWithheldCounts
  withheldIdentityLinks?: ErasureWithheldCounts
  /** Whole customer records joined to this subject only by a merge edge she cannot verify. */
  unverifiedMergeEdges?: number
  /** `false` means the visitor sweep ran over a SAMPLE, so absence was never proven. */
  visitorRosterProven?: boolean
  /** Kept precisely when the roster is unproven — the only route back to what it cannot name. */
  locatorLedgerRetained?: boolean
}

export type ErasureStatus = 'requested' | 'running' | 'completed' | 'needs_review' | 'failed'

/**
 * A receipt, as `toReceiptView` projects it: the stored row minus `configType` and
 * `manualReviewKey`, plus three fields stated so the surface need not infer them.
 *
 * ⚠️ **`terminal` is a SUPERSET of finality and `failed` is inside it.**
 * `TERMINAL_ERASURE_STATUS` is `['completed','needs_review','failed']`, but a `failed` may
 * be reopened — the engine throws so Lambda retries, and `markErasureRunning` /
 * `markErasureTerminal` both exclude `failed` from their "already finished" condition
 * precisely so a retry can. So `terminal` answers "should I stop polling", and
 * **only `completed | needs_review` answer "is this over"**. Use `isFinalErasure` rather
 * than reading `terminal` as done.
 *
 * ⚠️ **And a `failed` is not reliably still going either.** The async invoke is capped at
 * `maximum_retry_attempts = 2`, and the door itself writes `failed` with
 * `stores: [{ store: 'dispatch', ok: false }]` for a job it never dispatched at all. Only
 * that last case is discriminable. A surface must therefore claim neither outcome over a
 * `failed` and must leave the merchant a way to act — see
 * `components/knowledge/erasureReceipt.ts`, which holds the whole of that judgment.
 *
 * ⚠️ **`needsReview` is not a failure and is the COMMON case.** Everything provably owned
 * IS deleted; what is withheld is the *proof* that the sweep was exhaustive, because
 * `customerVisitorRosterProven` is `Array.isArray(marvaVisitorIds)` and only `linkVisitor`
 * maintains that field (MAR-195 is the backfill). A surface rendering it as a failure
 * breaks a published floor claim in one direction; one rendering an unqualified "Forgotten"
 * breaks it in the other.
 */
export interface ErasureReceipt {
  requestId: string
  scope: string
  customerId: string
  /** A MASKED email (`s••@example.com`) — enough to tell two people apart, never the address. */
  customerRef: string | null
  requestedAt: string
  requestedByUserId: string
  requestedByAuthType?: string
  requestedByRole: string
  requestedByRoleViaFallback?: boolean
  requestedReason?: string
  status: ErasureStatus
  startedAt?: string
  completedAt?: string
  attempts?: number
  customerFound?: boolean
  requiresManualHandling?: boolean
  /** One `UNRESOLVED.*` token, in the engine's severity order. Prose is built from it. */
  resolutionReason?: string | null
  residual?: ErasureResidual
  stores?: { store: string; ok: boolean; count?: number }[]
  failedSteps?: string[]
  /** That an operator's review record exists — never where it lives. */
  hasManualReviewRecord: boolean
  needsReview: boolean
  /** "Stop polling", NOT "this is over". See the warning above. */
  terminal: boolean
}

/**
 * Finality, which is narrower than `terminal`.
 *
 * ⚠️ It lives here, beside the type that carries the trap, rather than in the component:
 * the one thing a reader is likely to get wrong about this contract should not be a
 * condition written out by hand at each of its call sites.
 */
export function isFinalErasure(receipt: Pick<ErasureReceipt, 'status' | 'terminal'>): boolean {
  return receipt.terminal && receipt.status !== 'failed'
}

/** The 202 body. Accepted, not done — the receipt is the contract. */
export interface ErasureAccepted {
  requestId: string
  status: 'requested'
  scope: string
  customerId: string
  requestedAt: string
}

/** A product's expert card, returned WHOLE — it has no schema, so any field a writer adds arrives. */
export interface ProductKnowledge {
  card: Record<string, unknown>
  /** The string the agent is actually handed, from the shared `buildSummary`. */
  summary: string
  /**
   * ⚠️ `buildSummary` returns `''` for a card whose sections are all empty, which at the
   * string level is indistinguishable from "no card" — and the two mean opposite things.
   * The server states the difference rather than leaving it to be guessed.
   */
  summaryEmpty: boolean
  /**
   * Shadow-only product beliefs (Knowledge v6 §3.3), in the order to render them.
   *
   * ⚠️ `null` is NOT "believes nothing". A failed belief read does not blank the card — the
   * card is this route's primary content — so it answers `beliefs: null`, `counts: null`,
   * `beliefsUnavailable: true`. An empty `[]` only ever means the partition was read and
   * holds nothing visible.
   */
  beliefs: ProductBelief[] | null
  counts: BeliefCounts | null
  /** `true` exactly when the belief read failed. The server always sends it; `false` otherwise. */
  beliefsUnavailable?: boolean
  /**
   * What the card is missing, heaviest first — from the same section table `card-synthesizer`
   * scores `confidenceScore` with, so the gap and the score cannot disagree. Neither is
   * confidence. `fillable` is whether any writer can fill the section today.
   */
  gaps: ProductKnowledgeGap[]
}

export interface ProductKnowledgeGap {
  section: string
  weight: number
  fillable: boolean
}

// ── Least sure (Knowledge v6 §3.2, MAR-323) ─────────────────────────────────────────────

/** Who or what a least-sure item is about. A label is a NAME, never a belief. */
export type LeastSureLabel =
  | { customerId: string | null; firstName: string | null; lastName: string | null; emailMasked: string | null }
  | { productId: string; title: string | null }

/**
 * One subject — a person or a product — and its single least-sure ACTIVE belief.
 *
 * `confidence` is that belief's own number, on the one scale both subjects share.
 * ⚠️ `label: null` with `labelUnavailable` set is a person whose contact details could not
 * be decrypted: the item is real, only its name is withheld — never render it as anonymous.
 */
export interface LeastSureItem {
  scope: 'customer' | 'product'
  id: string
  label: LeastSureLabel | null
  labelUnavailable?: string
  confidence: number
  reason: { kind: 'belief'; belief: CustomerBelief | ProductBelief }
  freshness: string | null
  activeBeliefs: number
}

/**
 * How far one scope's read went. `exhaustive` is the server's claim that nothing it did not
 * read could displace the answer — stated, never inferred from `items.length`.
 * `unavailable` (products only) means the scope was not read at all, and says why.
 */
export interface LeastSureScopeBound {
  rowsScanned: number
  pages: number
  windowFrom: string | null
  windowTo: string | null
  candidates: number
  verified: number
  failed: number
  malformed: number
  exhaustive: boolean
  /** Products only: why the scope was not read (`workspace_account_unresolved`, `account_read_failed`). */
  unavailable?: string
  /** Customers only: candidates the person loader could not find. */
  unresolved?: number
  /** Products only: not this workspace's, or archived. */
  skipped?: number
  /** Products only: kept on the card's ownership proof with no verifiable catalogue row. */
  catalogueUnverified?: number
}

/**
 * How many subjects she has something to check about (MAR-389). A NUMBER only when the server
 * proved it exact; `{ atLeast }` is a floor — render it `N+`, never `N`. Absent from a server
 * that predates MAR-389, and then nothing is claimed.
 */
export type LeastSureCount = number | { atLeast: number }

export interface LeastSure {
  items: LeastSureItem[]
  count?: LeastSureCount
  bound: {
    limit: number
    verifyCap: number
    maxWindowPages: number
    /**
     * The subject a scoped read was asked for, or `null` for the unscoped read. A scoped
     * read carries ONLY its own entry below — the other subject was never counted.
     */
    scope?: LeastSureSubject | null
    customers?: LeastSureScopeBound
    products?: LeastSureScopeBound
    exhaustive: boolean
  }
}

/** `?scope=` on the least-sure read: one subject only. */
export type LeastSureSubject = 'customer' | 'product'

/**
 * One pattern (`?group=type`, MAR-389): ≥ 2 distinct subjects holding an ACTIVE belief of the
 * same `type` with identical wording (case / whitespace / trailing full stop aside — never
 * fuzzy). Each member is a least-sure item, so it settles through its own verdict route with its
 * own `expectedUpdatedAt`; there is no batch verdict route.
 */
export interface LeastSurePattern {
  key: string
  scope: LeastSureSubject
  type: string
  /** Her own words, from the least-sure member. */
  statement: string | null
  /** Distinct subjects — `members` can hold more when one subject says it twice. */
  subjects: number
  confidence: { min: number; max: number }
  members: LeastSureItem[]
}

export interface LeastSurePatterns {
  groups: LeastSurePattern[]
  /**
   * `groupsUnchecked` — window groups never verified because none could outrank the ones shown;
   * when non-zero, `groupsFound` is a floor (MAR-389 review).
   */
  bound: LeastSure['bound'] & { group: 'type'; groupsFound: number; groupsUnchecked?: number }
}

/** `?status=` on the timeline — the stored statuses. */
export type TimelineStatus = 'active' | 'confirmed' | 'refuted' | 'expired'

/** One belief she formed, as stored (MAR-389). `label: null` + `labelUnavailable` says why it cannot be named. */
export interface TimelineItem {
  scope: LeastSureSubject
  id: string
  label: LeastSureLabel | null
  labelUnavailable?: string
  /**
   * Where this row sits in its belief's chain of regenerations (every synthesis run writes a
   * fresh row, MAR-389 review): `again` — an older row of the SAME belief (type + evidence, or type + the same normalised wording) is
   * still stored, so she formed it again; `first` — none is. Absent when she could not check.
   */
  lineage?: 'first' | 'again'
  belief: CustomerBelief | ProductBelief
}

export interface TimelinePage {
  items: TimelineItem[]
  /** `null` only when the index ended. A short (even empty) page with a cursor is NOT the end. */
  cursor: string | null
  bound: {
    limit: number
    status: TimelineStatus | null
    /** Always `generatedAt` — the index has no `settledAt` order. */
    orderedBy: 'generatedAt'
    pages: number
    maxPages: number
    rowsScanned: number
    /** Rows past their TTL, not yet swept — not listed. */
    pastRetention?: number
    /** Expired rows of a belief she formed again — listed once, at the newest row. */
    renewedOmitted?: number
    /** Rows whose chain she could not check (no `lineage`). */
    lineageUnread?: number
  }
}

export const knowledgeApi = {
  /**
   * What she believes about one person, and what she has read.
   *
   * ⚠️ A failed belief read is a **500**, never an empty `beliefs` array: the server calls
   * `fetchHypotheses(..., { rethrow: true })` precisely so that "she believes nothing about
   * this person" cannot be a swallowed error on the one surface whose entire claim is the
   * opposite. A caller must therefore keep "could not read" and "nothing believed" apart.
   *
   * ⚠️ **Render `beliefs` in the order given.** The read collapses duplicate rows per
   * `hypothesisId` through the shared `selectHypothesisRow`, and the verdict re-picks on the
   * `updatedAt` that collapse produced. A client-side sort or re-pick between the two aims
   * the verdict at a row the merchant never saw.
   */
  getCustomerKnowledge: (
    workspaceId: string,
    customerId: string,
    opts: { includeSettled?: boolean } = {},
    client = apiClient
  ): Promise<ApiResponse<CustomerKnowledge>> =>
    client.get(
      `/workspaces/${workspaceId}/knowledge/customers/${encodeURIComponent(customerId)}` +
        (opts.includeSettled ? '?includeSettled=true' : '')
    ),

  /**
   * The merchant's verdict on a belief — the writer the `refuted` rail never had.
   *
   * ⚠️ `expectedUpdatedAt` must be the `updatedAt` of the belief that was RENDERED, passed
   * through untouched. It is the primary selector as well as the concurrency token, so a
   * re-derived or freshest-known value settles a different row than the one on screen.
   *
   * Three outcomes, and only one of them is a failure: 200 settled, 200 with
   * `alreadySettled: true` (a replay — still a success), and 409 `hypothesis_write_conflict`
   * whose `details.current` carries the belief **as it now stands**, so the surface can
   * re-render in place rather than sending the merchant to reload.
   */
  settleBelief: (
    workspaceId: string,
    customerId: string,
    hypothesisId: string,
    data: { verdict: 'refuted' | 'confirmed'; expectedUpdatedAt: string | null },
    client = apiClient
  ): Promise<ApiResponse<SettleBeliefResult>> =>
    client.post(
      `/workspaces/${workspaceId}/knowledge/customers/${encodeURIComponent(customerId)}` +
        `/hypotheses/${encodeURIComponent(hypothesisId)}`,
      data
    ),

  /**
   * Forget one person, in a single action.
   *
   * ⚠️ **202, not 200.** Twenty steps over ~20 tables and two buckets against a
   * non-overridable 29s API Gateway limit — so the door ACCEPTS the work and the receipt is
   * the contract. `success: true` here means "started", never "done", and a caller that
   * renders it as done is claiming something the platform has not checked yet.
   *
   * ⚠️ `confirmCustomerId` is built from the SAME `customerId` the path is built from, one
   * argument, so the two cannot drift into a `confirmation_mismatch` the merchant cannot
   * act on. The human confirmation is the arm-then-confirm click in the UI; the server's
   * echo check is a guard against a malformed request, not a second question for the
   * merchant.
   *
   * `scope` is sent explicitly although the server defaults it: `ERASURE_SCOPES` is
   * `['single_customer']` today and MAR-59 adds `selected` / `all_customers`, so naming the
   * one this call means keeps a future default change from silently widening it.
   */
  requestErasure: (
    workspaceId: string,
    customerId: string,
    client = apiClient
  ): Promise<ApiResponse<ErasureAccepted>> =>
    client.post(
      `/workspaces/${workspaceId}/knowledge/customers/${encodeURIComponent(customerId)}/erasure`,
      { confirmCustomerId: customerId, scope: 'single_customer' }
    ),

  /** One receipt, by request id. Poll it until `terminal` — see the trap on `ErasureReceipt`. */
  getErasureReceipt: (
    workspaceId: string,
    requestId: string,
    client = apiClient
  ): Promise<ApiResponse<{ receipt: ErasureReceipt }>> =>
    client.get(`/workspaces/${workspaceId}/knowledge/erasures/${encodeURIComponent(requestId)}`),

  /**
   * The workspace's erasure history.
   *
   * ⚠️ `truncated` is **stated by the server**, never inferred from `receipts.length`. The
   * server clamps `limit` to 1–100 itself, so a client asking for more does not get more —
   * and a list that quietly looks complete is the opposite of an audit trail.
   *
   * ⚠️ No role check on this route, deliberately: performing an erasure is gated, reading
   * what was already done is the audit trail, and an audit trail only the people who can
   * fire the action may read is not much of one.
   */
  listErasures: (
    workspaceId: string,
    limit = 20,
    client = apiClient
  ): Promise<ApiResponse<{ receipts: ErasureReceipt[]; truncated: boolean }>> =>
    client.get(`/workspaces/${workspaceId}/knowledge/erasures?limit=${limit}`),

  /**
   * One product's expert card, the summary the agent would be given, its beliefs and its gaps.
   * `includeSettled` widens the beliefs exactly as on the customer route (only the literal
   * `true` is sent); the card and the gaps are the same either way.
   */
  getProductKnowledge: (
    workspaceId: string,
    productId: string,
    opts: { includeSettled?: boolean } = {},
    client = apiClient
  ): Promise<ApiResponse<ProductKnowledge>> =>
    client.get(
      `/workspaces/${workspaceId}/knowledge/products/${encodeURIComponent(productId)}` +
        (opts.includeSettled ? '?includeSettled=true' : '')
    ),

  /**
   * The merchant's verdict on a product belief — the customer verdict's contract by subject:
   * same body, same three outcomes (200, 200 `alreadySettled`, 409 with `details.current`).
   * A product this workspace has no expert card for is a 404 `product_card_not_found`.
   *
   * ⚠️ `beliefId` and `expectedUpdatedAt` come off the `ProductBelief` that was RENDERED,
   * untouched — the same rule as `settleBelief`.
   */
  settleProductBelief: (
    workspaceId: string,
    productId: string,
    beliefId: string,
    data: { verdict: 'refuted' | 'confirmed'; expectedUpdatedAt: string | null },
    client = apiClient
  ): Promise<ApiResponse<SettleBeliefResult<ProductBelief>>> =>
    client.post(
      `/workspaces/${workspaceId}/knowledge/products/${encodeURIComponent(productId)}` +
        `/beliefs/${encodeURIComponent(beliefId)}`,
      data
    ),

  /**
   * Where she is least sure, people and products on one confidence scale.
   *
   * `limit` is clamped by the server (1–50, default 10) and never refused, so asking for more
   * does not get more. `scope` (MAR-325) reads one subject alone, with `limit`, the verify
   * cap and `exhaustive` all describing that subject; an unknown scope is a 400. ⚠️ Read
   * `bound` before calling the list complete: a scope can be non-exhaustive, or
   * `unavailable` and not read at all, on a 200.
   */
  getLeastSure: (
    workspaceId: string,
    limit?: number,
    scope?: LeastSureSubject,
    client = apiClient
  ): Promise<ApiResponse<LeastSure>> => {
    const qs = new URLSearchParams()
    if (limit !== undefined) qs.set('limit', String(limit))
    if (scope) qs.set('scope', scope)
    const q = qs.toString()
    return client.get(`/workspaces/${workspaceId}/knowledge/least-sure${q ? `?${q}` : ''}`)
  },

  /**
   * Patterns she's seeing (MAR-389): the least-sure window grouped by belief type and identical
   * wording across subjects (`?group=type`). `limit` caps the patterns. ⚠️ Read `bound` before
   * calling the list complete, exactly as on `getLeastSure`.
   */
  getLeastSurePatterns: (
    workspaceId: string,
    scope?: LeastSureSubject,
    limit?: number,
    client = apiClient
  ): Promise<ApiResponse<LeastSurePatterns>> => {
    const qs = new URLSearchParams({ group: 'type' })
    if (scope) qs.set('scope', scope)
    if (limit !== undefined) qs.set('limit', String(limit))
    return client.get(`/workspaces/${workspaceId}/knowledge/least-sure?${qs.toString()}`)
  },

  /**
   * What she learned (MAR-389): every belief, newest GENERATION first. ⚠️ Ordered by when she
   * formed each one, never by when it was settled, and `expired` lists only rows still stored.
   * `cursor` is the opaque value the previous page returned, passed back untouched.
   */
  getTimeline: (
    workspaceId: string,
    opts: { status?: TimelineStatus; cursor?: string | null; limit?: number } = {},
    client = apiClient
  ): Promise<ApiResponse<TimelinePage>> => {
    const qs = new URLSearchParams()
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit))
    if (opts.status) qs.set('status', opts.status)
    if (opts.cursor) qs.set('cursor', opts.cursor)
    const q = qs.toString()
    return client.get(`/workspaces/${workspaceId}/knowledge/timeline${q ? `?${q}` : ''}`)
  },
}

/**
 * Which act a refusal refused.
 *
 * ⚠️ It exists for exactly one reason, and it is not decoration: **two tokens are shared by
 * a read and an erasure and mean different things on each.** `pii_decrypt_unavailable` on a
 * read means a record is not being shown; on an erasure it means **nothing was destroyed
 * and no receipt was written** — the door refuses before it dispatches, because an
 * all-or-nothing anchor set is the only honest contract when the alternative is erasing
 * most of a person and reporting success. Saying "this record is not being shown" over a
 * refused erasure would leave a merchant with no idea whether their customer still exists.
 */
export type KnowledgeAct = 'read' | 'erasure'

/**
 * The server's refusal, said in the merchant's terms.
 *
 * ⚠️ Keyed on `details.reason` — the stable token — never on `error`, which is prose. This
 * is the ONLY mapping of the knowledge tokens in the web app; it follows the shape
 * `AuthorityPanel.refusalMessage` established: a `switch` with a `default` that passes the
 * server's own prose through, so a token added server-side degrades to something true
 * rather than to a lie.
 *
 * @param reason `(res.details as { reason?: unknown })?.reason`
 * @param fallback the server's `error` prose
 * @param details the refusal's own `details`, which nests `accessReason` and `roleReason`
 * @param act what was being attempted — see `KnowledgeAct`
 */
export function knowledgeRefusal(
  reason: unknown,
  fallback: string,
  details?: { accessReason?: unknown; roleReason?: unknown } | null,
  act: KnowledgeAct = 'read'
): string {
  const accessReason = details?.accessReason
  switch (reason) {
    case 'customer_not_found':
      return act === 'erasure'
        ? 'There is no such customer in this workspace, so there was nothing to erase.'
        : 'There is no such customer in this workspace.'
    case 'pii_decrypt_unavailable':
      // 503, and the honest word is "yet" — a missing key is transient config, and the
      // server refuses rather than showing an unnamed person.
      //
      // ⚠️ On the erasure door the same token carries a much stronger promise, and it is the
      // one the merchant needs first: the door reads and decrypts BEFORE it dispatches, and
      // on failure it writes no receipt and invokes nothing. Four of the engine's steps
      // match on a plaintext email or phone, so an erasure run without readable anchors
      // would sweep nothing on those four and still report clean. Refusing early is the
      // whole design, and "nothing has been changed" is the part that must be said.
      return act === 'erasure'
        ? 'Nothing has been erased. Marva holds contact details for this person that she could not read just now, and without them she cannot prove an erasure was complete — so she refused to start one rather than half-finish it. This is our problem, not yours; try again shortly.'
        : 'Her contact details are stored encrypted and could not be read just now, so this record is not being shown rather than shown unnamed. Try again shortly.'

    // ── Erasure ─────────────────────────────────────────────────────────────────────────
    case 'erasure_requires_workspace_admin':
      // ⚠️ `details.roleReason` is nested BY THE SERVER and the three values are three
      // different situations for the person reading this. "You are not a member of this
      // workspace" and "we could not check" must not both render as "ask an admin": only
      // one of them is about them, and only one of them is worth retrying.
      switch (details?.roleReason) {
        case 'no_workspace_membership':
          return 'Erasing a customer is limited to this workspace\'s owner or an admin, and you are not listed in it at all. Nothing has been erased.'
        case 'role_check_failed':
          return 'Marva could not check what you are allowed to do here, so she did nothing rather than guess. Nothing has been erased — try again shortly.'
        case 'insufficient_workspace_role':
          return 'Erasing a customer is limited to this workspace\'s owner or an admin. Nothing has been erased — ask one of them to do it.'
        default:
          return 'Erasing a customer is limited to this workspace\'s owner or an admin. Nothing has been erased.'
      }
    case 'confirmation_mismatch':
      // Ours, not theirs. The client builds the path and the confirmation from one value,
      // so this can only mean the two came apart in our code.
      return 'Nothing has been erased — the confirmation did not name the person on screen. Close this drawer, reopen it and try again.'
    case 'invalid_request':
      return 'Nothing has been erased — that is not an erasure Marva can carry out.'
    case 'erasure_already_in_flight':
      // Never a failure to the merchant: what they asked for is already happening.
      return 'Marva is already erasing this person. Nothing new was started.'
    case 'erasure_request_not_found':
      return 'That erasure receipt is no longer in this workspace.'
    case 'workspace_account_unresolved':
      // A broken invariant on our side — the workspace row has no account. Deliberately
      // NOT phrased as an access problem: the caller passed both the access check and the
      // role gate, and telling them they lack access to a workspace they are demonstrably
      // inside is a refusal they cannot recognise as their own situation.
      return 'Nothing has been erased. This workspace is missing something Marva needs to reach every place she keeps data, and she will not run an erasure she cannot prove complete. This one is ours to fix.'

    case 'invalid_verdict':
      return 'That is not a verdict she can record.'
    case 'missing_expected_updated_at':
      // Ours, not theirs: the client is supposed to send back the `updatedAt` it rendered.
      return 'Nothing was recorded — the belief on screen is missing the version stamp the verdict needs. Reopen this customer and try again.'
    case 'hypothesis_not_found':
      return 'She no longer holds this belief, so there was nothing to settle.'
    case 'hypothesis_write_conflict':
      return 'She changed her mind about this while you were looking, and nothing was written. This is what she believes now.'
    case 'workspace_access_denied':
      return accessReason
        ? `You do not have access to this workspace (${String(accessReason)}).`
        : 'You do not have access to this workspace.'
    case 'internal_error':
      // ⚠️ Same token, opposite promise again. On a read the honest statement is that
      // nothing is being shown; on the erasure door the handler's own catch answers
      // "The erasure could not be started. Nothing has been changed." — which is the fact
      // the merchant actually needs, and it is not the fact "nothing is shown" carries.
      return act === 'erasure'
        ? 'Something on our side failed, so the erasure was not started. Nothing has been changed — try again shortly.'
        : 'Something on our side failed, so nothing is shown rather than a guess.'
    default:
      // The server's own prose. Never invented here — a token this switch has not met yet
      // must degrade to what the server said, not to a message that claims to know why.
      return fallback
  }
}

// Export default api object for backward compatibility
export const api = {
  get: <T>(endpoint: string, options?: { params?: Record<string, any> }) => {
    let url = endpoint
    if (options?.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Handle complex objects by JSON stringifying them
          const paramValue = typeof value === 'object' ? JSON.stringify(value) : String(value)
          searchParams.append(key, paramValue)
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString
      }
    }
    return apiClient.get<T>(url)
  },
  post: <T>(endpoint: string, data?: any) => apiClient.post<T>(endpoint, data),
  put: <T>(endpoint: string, data?: any) => apiClient.put<T>(endpoint, data),
  delete: <T>(endpoint: string) => apiClient.delete<T>(endpoint)
}
